import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import StatCard from '../../../components/common/StatCard';
import AlertToast from '../../../components/common/AlertToast';
import PolicyTab from '../../../components/common/PolicyTab';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useActivityTracker } from '../../../hooks/useActivityTracker';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const fmtTime = d => d ? new Date(d).toLocaleTimeString('en-IN',{timeZone:'Asia/Kolkata',hour:'2-digit',minute:'2-digit'}) : '—';
const inputTime = d => d ? new Date(d).toLocaleTimeString('en-GB', { timeZone:'Asia/Kolkata', hour:'2-digit', minute:'2-digit', hourCycle:'h23' }) : '';
const fmtMins = m => { if(!m) return '0h 0m'; const h=Math.floor(m/60),mn=m%60; return `${h}h ${mn}m`; };
const todayISO = () => new Date().toISOString().split('T')[0];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const STATUS_BADGE = { present:'success', absent:'danger', half_day:'warning', leave:'info', on_leave:'info', weekend:'secondary', holiday:'purple', future:'light', permission:'info' };
const STATUS_BG    = { present:'#198754', absent:'#dc3545', half_day:'#fd7e14', leave:'#0d6efd', on_leave:'#0d6efd', weekend:'#6c757d', holiday:'#6f42c1', future:'#e9ecef', permission:'#0d6efd' };
const STATUS_LABEL = { present:'Present', absent:'Absent', half_day:'Half Day', leave:'Leave', on_leave:'Leave', weekend:'Weekend', holiday:'Holiday', future:'Future', permission:'Permission' };
const minsToStatus = (m) => m >= 480 ? 'present' : m >= 240 ? 'half_day' : m > 0 ? 'permission' : 'absent';
const ACTIVITY_REASONS = ['meeting','enquiry','demo_class','management_task','other'];
const REASON_LABELS    = { meeting:'Meeting', enquiry:'Enquiry', demo_class:'Demo Class', management_task:'Management Task', other:'Other' };

// Day-of-year — used to rotate happy messages so the line changes daily
// but stays stable inside the same day (no jumping around on every render).
const dayOfYear = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  return Math.floor((now - start) / 86400000);
};
const HAPPY_MESSAGES = [
  "Have a fantastic day, {name}! Let's make it count. 🌞",
  "Welcome back, {name}! Coffee's brewing — let's go. ☕",
  "{name}, today is yours. Crush it. 🚀",
  "Glad to see you, {name}. Wishing you a productive day. 🌱",
  "Hello {name}! Small wins all day add up to a great one. 🌟",
  "{name}, take it one task at a time. You've got this. 💪",
  "Good vibes only — welcome aboard, {name}! 🎯",
  "{name}, your team is lucky to have you today. 🙌",
  "Fresh start, fresh focus, {name}. Here we go. 🍃",
  "Hi {name} — let's turn this day into a great story. 📖",
  "Big things ahead, {name}. Keep it steady. 🧭",
  "{name}, deep breath, kind smile, full focus. ✨",
  "Welcome, {name}! Ready to make today count? 🔥",
  "{name}, may your inbox be light and your wins be heavy. 📬",
  "Stay curious, stay kind, {name}. Have a great day. 🌈",
  "Good to have you back, {name}. Today is a clean slate. 📒",
  "{name}, tiny step + tiny step = giant leap. Go. 🦶",
  "Hi {name} — chin up, music on, let's roll. 🎧",
  "Be the energy you want today, {name}. ⚡",
  "{name}, you're showing up — that's already half the win. 🏆",
];
const LUNCH_MESSAGES = [
  "Enjoy your lunch, {name}! 🍱",
  "Bon appétit, {name}! 🥗",
  "Fuel up well, {name} — break is well earned. 🍵",
  "Take your time, {name}. Eat, breathe, reset. 🌿",
  "Lunch wisely, {name}! Hydrate too. 💧",
  "Good break, {name} — see you when you're back. 🍴",
  "Step away, savor it, {name}. 🍜",
];
const pickDaily = (pool, name) =>
  (pool[dayOfYear() % pool.length] || '').replace('{name}', name || 'there');

// ─── Device fingerprinting ────────────────────────────────────────────────────
// Two separate identifiers are sent with every clock-in:
//
//  1. deviceFingerprint  — a UUID generated ONCE per browser profile and stored
//     in localStorage. This is the "registration handle" that the admin approves.
//     It stays the same across page reloads so long as localStorage isn't cleared.
//
//  2. hardwareHash — an FNV hash computed FRESH every clock-in from hardware/OS
//     signals (screen, timezone, CPU, memory, platform, touch, GPU, canvas).
//     Stored by the server at registration time and compared on every subsequent
//     clock-in. If someone copies the localStorage UUID to a different machine,
//     this hash won't match → clock-in blocked even with an "approved" UUID.
//
//  3. deviceInfo — structured breakdown of browser/OS details stored with the
//     registration so admins can see "Chrome 124 on Windows 11, 1920×1080"
//     instead of a raw user-agent string when deciding whether to approve.

// FNV-1a 32-bit — fast, good distribution for short string sets.
const fnv32 = (str) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
};

// Parse browser name + major version from user-agent.
const parseBrowser = (ua) => {
  if (/Edg\//i.test(ua))     return 'Edge '    + (ua.match(/Edg\/(\d+)/)?.[1] || '');
  if (/OPR\//i.test(ua))     return 'Opera '   + (ua.match(/OPR\/(\d+)/)?.[1]  || '');
  if (/Chrome\//i.test(ua))  return 'Chrome '  + (ua.match(/Chrome\/(\d+)/)?.[1]  || '');
  if (/Firefox\//i.test(ua)) return 'Firefox ' + (ua.match(/Firefox\/(\d+)/)?.[1] || '');
  if (/Safari\//i.test(ua))  return 'Safari '  + (ua.match(/Version\/(\d+)/)?.[1] || '');
  return 'Unknown Browser';
};

// Parse OS name + version.
const parseOS = (ua) => {
  if (/Windows NT 10\.0/i.test(ua)) return 'Windows 10/11';
  if (/Windows NT 6\.3/i.test(ua))  return 'Windows 8.1';
  if (/Windows NT 6\.1/i.test(ua))  return 'Windows 7';
  if (/Windows/i.test(ua))          return 'Windows';
  if (/iPhone OS ([\d_]+)/i.test(ua)) return 'iOS ' + ua.match(/iPhone OS ([\d_]+)/i)[1].replace(/_/g, '.');
  if (/iPad.*OS ([\d_]+)/i.test(ua))  return 'iPadOS ' + ua.match(/iPad.*OS ([\d_]+)/i)[1].replace(/_/g, '.');
  if (/Android ([\d.]+)/i.test(ua))   return 'Android ' + ua.match(/Android ([\d.]+)/i)[1];
  if (/Mac OS X ([\d_]+)/i.test(ua))  return 'macOS ' + ua.match(/Mac OS X ([\d_]+)/i)[1].replace(/_/g, '.');
  if (/Linux/i.test(ua))              return 'Linux';
  return 'Unknown OS';
};

// Attempt a WebGL renderer string — gives GPU model on most desktops/mobiles.
const getGPUInfo = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return '';
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    return ext ? (gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || '') : '';
  } catch (_) { return ''; }
};

// Canvas pixel hash — renders a small scene; output differs per OS/GPU/driver.
const getCanvasHash = () => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200; canvas.height = 50;
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('TSLMS-fp', 2, 15);
    ctx.fillStyle = 'rgba(102,204,0,0.7)';
    ctx.fillText('TSLMS-fp', 4, 17);
    return fnv32(canvas.toDataURL());
  } catch (_) { return ''; }
};

// Collect all hardware/OS signals and compute a composite hash.
// This runs fresh on every clock-in so it always reflects the current machine.
const computeHardwareHash = () => {
  const gpu    = getGPUInfo();
  const canvas = getCanvasHash();
  const tz     = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  const signals = [
    navigator.userAgent,
    navigator.platform || '',
    navigator.language || '',
    (navigator.languages || []).join(','),
    `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
    String(navigator.hardwareConcurrency || 0),
    String(navigator.deviceMemory || 0),
    String(navigator.maxTouchPoints || 0),
    tz,
    gpu,
    canvas,
  ].join('||');
  return fnv32(signals);
};

// Structured device info for admin display.
const collectDeviceInfo = () => {
  const ua = navigator.userAgent;
  return {
    browser:     parseBrowser(ua),
    os:          parseOS(ua),
    screen:      `${window.screen.width}×${window.screen.height} ${window.screen.colorDepth}-bit`,
    timezone:    Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    language:    navigator.language || '',
    cores:       navigator.hardwareConcurrency || 0,
    memory:      navigator.deviceMemory || 0,
    platform:    navigator.platform || '',
    touchPoints: navigator.maxTouchPoints || 0,
    gpu:         getGPUInfo(),
  };
};

// Stable localStorage UUID — generated once per browser profile, persists
// across page reloads. This is what the admin actually approves.
const getDeviceId = () => {
  let id = localStorage.getItem('tslms_device_id');
  if (id) return id;
  id = computeHardwareHash() + '-' + Date.now().toString(36);
  localStorage.setItem('tslms_device_id', id);
  return id;
};

const getDeviceName = () => {
  const info = collectDeviceInfo();
  return info.browser && info.os ? `${info.browser} on ${info.os}` : (info.os || 'Unknown Device');
};

// ─── LiveClock ────────────────────────────────────────────────────────────────
const LiveClock = ({ loginTime }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!loginTime) return;
    const tick = () => setElapsed(Math.round((Date.now() - new Date(loginTime)) / 60000));
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [loginTime]);
  const h = Math.floor(elapsed/60), m = elapsed%60;
  return <span className="fw-bold text-success">{String(h).padStart(2,'0')}:{String(m).padStart(2,'0')}</span>;
};

// ─── BSModal ──────────────────────────────────────────────────────────────────
const BSModal = ({ show, onClose, title, size='', children, footer }) =>
  show ? (
    <div className="modal fade show d-block" style={{background:'rgba(0,0,0,0.45)',zIndex:1050}}
      onClick={e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div className={`modal-dialog modal-dialog-scrollable modal-dialog-centered ${size}`}>
        <div className="modal-content">
          <div className="modal-header"><h5 className="modal-title">{title}</h5><button className="btn-close" onClick={onClose}/></div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  ) : null;

// ─── AdjustDialog ─────────────────────────────────────────────────────────────
const AdjustDialog = ({ show, record, onClose, onSuccess, onAlert }) => {
  const [form, setForm] = useState({ totalHours:'', notes:'', status:'' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (show && record) setForm({
      totalHours: record.totalWorkedMinutes ? (record.totalWorkedMinutes / 60).toFixed(1) : '',
      notes: '',
      status: record.status || 'present',
    });
  }, [show, record]);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const handleSave = async () => {
    setSaving(true);
    try {
      const hrs = parseFloat(form.totalHours);
      const payload = {
        status: form.status,
        notes:  form.notes,
        ...(form.totalHours !== '' && !isNaN(hrs) ? { totalWorkedMinutes: Math.round(hrs * 60) } : {}),
      };
      const r = await api.patch(`/training/work-attendance/${record._id}/adjust`, payload);
      if(r.data?.success!==false){ onSuccess(r.data?.data||r.data); onClose(); }
      else onAlert(r.data?.message||'Failed.','danger');
    } catch(e){ onAlert(e.response?.data?.message||'Failed.','danger'); }
    setSaving(false);
  };
  return (
    <BSModal show={show} onClose={onClose} title="Adjust Attendance Record"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?<span className="spinner-border spinner-border-sm"/>:'Save & Notify'}</button></>}>
      <div className="mb-3">
        <label className="form-label fw-semibold">Status</label>
        <select className="form-select" value={form.status} onChange={e=>set('status',e.target.value)}>
          {['present','absent','half_day','holiday','leave','weekend'].map(s=><option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
        </select>
      </div>
      <div className="mb-3">
        <label className="form-label fw-semibold">Total Hours Worked</label>
        <input className="form-control" type="number" min={0} max={24} step={0.5}
          value={form.totalHours} onChange={e=>set('totalHours',e.target.value)}
          placeholder="e.g. 8 or 4.5"/>
        <div className="form-text">Replaces the current value. Leave blank to keep unchanged.</div>
      </div>
      <div>
        <label className="form-label fw-semibold">Reason / Note</label>
        <textarea className="form-control" rows={2} value={form.notes} onChange={e=>set('notes',e.target.value)}
          placeholder="Reason for adjustment (sent to employee)"/>
      </div>
    </BSModal>
  );
};

// ─── ActivityApproveDialog ────────────────────────────────────────────────────
const ActivityApproveDialog = ({ show, activity, onClose, onSuccess, onAlert }) => {
  const [form, setForm] = useState({ actualMins:'', approvalNote:'' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if(show) setForm({ actualMins:activity?.expectedDurationMinutes||'', approvalNote:'' }); }, [show, activity]);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const handle = async (action) => {
    setSaving(true);
    try {
      const r = await api.patch(`/training/work-attendance/activities/${activity._id}/${action}`, form);
      if(r.data?.success!==false){ onSuccess(); onClose(); }
      else onAlert(r.data?.message||'Failed.','danger');
    } catch(e){ onAlert(e.response?.data?.message||'Failed.','danger'); }
    setSaving(false);
  };
  return (
    <BSModal show={show} onClose={onClose} title="Review Activity Request"
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-danger" onClick={()=>handle('reject')} disabled={saving}>Reject</button>
        <button className="btn btn-success" onClick={()=>handle('approve')} disabled={saving}>{saving?<span className="spinner-border spinner-border-sm"/>:'Approve'}</button>
      </>}>
      {activity && <div className="mb-3 p-2 bg-light rounded">
        <strong>{REASON_LABELS[activity.reason]||activity.reason}</strong>
        <p className="mb-1 small text-muted">{activity.description}</p>
        <small>Requested: {fmtMins(activity.expectedDurationMinutes||0)}</small>
      </div>}
      <div className="mb-3">
        <label className="form-label">Actual Minutes</label>
        <input className="form-control" type="number" value={form.actualMins} onChange={e=>set('actualMins',e.target.value)}/>
      </div>
      <div>
        <label className="form-label">Approval Note</label>
        <textarea className="form-control" rows={2} value={form.approvalNote} onChange={e=>set('approvalNote',e.target.value)}/>
      </div>
    </BSModal>
  );
};

// ─── ManualEntryDialog ────────────────────────────────────────────────────────
const OVERRIDE_REASONS = [
  { value:'Power cut',      label:'Power Cut' },
  { value:'System offline', label:'System Offline / Downtime' },
  { value:'Network issue',  label:'Network / Internet Issue' },
  { value:'Emergency',      label:'Emergency / Personal Emergency' },
  { value:'Other',          label:'Other' },
];
const ManualEntryDialog = ({ show, onClose, onSuccess, onAlert, staffList }) => {
  const blank = { userId:'', date:todayISO(), status:'present', hoursWorked:'', overrideReason:'Power cut', notes:'' };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const handleSave = async () => {
    if(!form.userId) return onAlert('Please select a staff member.','warning');
    setSaving(true);
    try {
      const hrs = parseFloat(form.hoursWorked);
      const payload = {
        userId:             form.userId,
        date:               form.date,
        status:             form.status,
        totalWorkedMinutes: !isNaN(hrs) && hrs > 0 ? Math.round(hrs * 60) : 0,
        overrideReason:     form.overrideReason,
        notes:              form.notes,
      };
      const r = await api.post('/training/work-attendance/admin', payload);
      if(r.data?.success!==false){ onSuccess(); onClose(); setForm(blank); }
      else onAlert(r.data?.message||'Failed.','danger');
    } catch(e){ onAlert(e.response?.data?.message||'Failed.','danger'); }
    setSaving(false);
  };
  return (
    <BSModal show={show} onClose={onClose} title="Manual Attendance Entry (Admin Override)"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?<span className="spinner-border spinner-border-sm"/>:'Save & Notify Employee'}</button></>}>
      <div className="alert alert-info py-2 small mb-3">
        <i className="bi bi-info-circle me-1"/>Use this when an employee couldn't clock in (power cut, system offline, etc.). The employee will receive a notification.
      </div>
      <div className="mb-3">
        <label className="form-label fw-semibold">Staff Member <span className="text-danger">*</span></label>
        <select className="form-select" value={form.userId} onChange={e=>set('userId',e.target.value)}>
          <option value="">Select staff…</option>
          {staffList.map(s=><option key={s._id} value={s._id}>{s.firstName} {s.lastName} ({s.roleKey||'—'})</option>)}
        </select>
      </div>
      <div className="row g-3 mb-3">
        <div className="col-6">
          <label className="form-label fw-semibold">Date <span className="text-danger">*</span></label>
          <input className="form-control" type="date" value={form.date} onChange={e=>set('date',e.target.value)}/>
        </div>
        <div className="col-6">
          <label className="form-label fw-semibold">Status</label>
          <select className="form-select" value={form.status} onChange={e=>set('status',e.target.value)}>
            {['present','half_day','absent','holiday','leave'].map(s=><option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
          </select>
        </div>
      </div>
      <div className="row g-3 mb-3">
        <div className="col-6">
          <label className="form-label fw-semibold">Hours Worked</label>
          <input className="form-control" type="number" min={0} max={24} step={0.5}
            value={form.hoursWorked} onChange={e=>set('hoursWorked',e.target.value)}
            placeholder="e.g. 8 or 4.5"/>
          <div className="form-text">Leave blank if unknown / absent</div>
        </div>
        <div className="col-6">
          <label className="form-label fw-semibold">Reason</label>
          <select className="form-select" value={form.overrideReason} onChange={e=>set('overrideReason',e.target.value)}>
            {OVERRIDE_REASONS.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="form-label">Additional Notes</label>
        <textarea className="form-control" rows={2} value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Optional — any extra context for this record"/>
      </div>
    </BSModal>
  );
};

// ─── Calendar Grid ────────────────────────────────────────────────────────────
const renderCalendar = (days) => {
  if (!days?.length) return null;
  const firstDow = new Date(days[0].date).getDay();
  const cells = [...Array(firstDow).fill(null), ...days];
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i+7));
  return (
    <div>
      <div className="row g-1 mb-1">
        {DAYS.map(d=><div key={d} className="col text-center small fw-semibold text-muted">{d}</div>)}
      </div>
      {weeks.map((week,wi)=>(
        <div key={wi} className="row g-1 mb-1">
          {week.map((day,di)=>{
            if (!day) return <div key={di} className="col"/>;
            const bg = STATUS_BG[day.status] || STATUS_BG.future;
            const isLight = ['future','weekend'].includes(day.status);
            return (
              <div key={di} className="col">
                <div style={{background:bg, color:isLight?'#333':'#fff', minHeight:52, borderRadius:4, padding:'4px 5px',
                  border: day.isToday ? '2px solid #000' : '1px solid transparent', cursor:'default'}}>
                  <div className="fw-bold" style={{fontSize:13}}>{new Date(day.date).getDate()}</div>
                  {day.totalMins>0 && <div style={{fontSize:10}}>{fmtMins(day.totalMins)}</div>}
                  {day.status && !['future','weekend'].includes(day.status) &&
                    <div style={{fontSize:9,textTransform:'capitalize'}}>{day.status.replace('_',' ')}</div>}
                </div>
              </div>
            );
          })}
          {week.length < 7 && Array(7-week.length).fill(0).map((_,i)=><div key={`e${i}`} className="col"/>)}
        </div>
      ))}
    </div>
  );
};

// ─── Idle Warning Dialog ─────────────────────────────────────────────────────
const IdleWarningDialog = ({ show, lastActivity, onDismiss, onClockOut }) => {
  if (!show) return null;
  const lastActivityDate = lastActivity ? new Date(lastActivity) : null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div className="card shadow-lg" style={{ maxWidth: 450, width: '90%' }}>
        <div className="card-header bg-warning">
          <h5 className="mb-0"><i className="bi bi-exclamation-triangle-fill me-2"/>Inactivity Detected</h5>
        </div>
        <div className="card-body">
          <p className="mb-2">We haven't detected any activity from you for over <strong>2 hours</strong>.</p>
          {lastActivityDate && (
            <p className="mb-2 text-muted small">
              Last interaction was at: <strong>{fmtTime(lastActivityDate)}</strong> on {fmtDate(lastActivityDate)}
            </p>
          )}
          <p className="mb-0">Would you like to continue working or clock out?</p>
          <div className="alert alert-info py-2 mt-3 mb-0 small">
            <i className="bi bi-info-circle me-1"/>
            If you don't respond within <strong>5 minutes</strong>, the system will automatically clock you out using your last interaction time.
          </div>
        </div>
        <div className="card-footer d-flex gap-2 justify-content-end">
          <button className="btn btn-outline-secondary" onClick={onDismiss}>
            <i className="bi bi-hand-thumbs-up me-1"/>I'm Still Working
          </button>
          <button className="btn btn-danger" onClick={onClockOut}>
            <i className="bi bi-stop-fill me-1"/>Clock Out Now
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── SendReportEmailDialog ────────────────────────────────────────────────────
const SendReportEmailDialog = ({ show, data, onClose, onSuccess, onAlert }) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (show) {
      setMessage(`Dear ${data?.employeeName || 'Employee'},\n\nPlease find attached your monthly attendance report for the month of ${new Date().toLocaleString('en-IN', { month: 'long' })}.\n\nIf you have any queries or discrepancies, please feel free to reach out to the HR team.\n\nBest Regards,\nHR Department`);
      setSending(false);
    }
  }, [show, data]);

  const handleSend = async () => {
    setSending(true);
    try {
      const r = await api.post(`/training/work-attendance/reports/${data?.reportId}/send-email`, { customMessage: message });
      if (r.data?.success !== false) {
        onSuccess();
        onClose();
      } else {
        onAlert(r.data?.message || 'Failed to send email.', 'danger');
      }
    } catch (e) {
      onAlert(e.response?.data?.message || 'Failed to send email.', 'danger');
    }
    setSending(false);
  };

  return (
    <BSModal show={show} onClose={onClose} title="Send Report via Email" size="modal-lg"
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSend} disabled={sending}>
          {sending ? <span className="spinner-border spinner-border-sm"/> : <><i className="bi bi-envelope me-1"/> Send Email</>}
        </button>
      </>}>
      <div className="mb-3">
        <label className="form-label fw-semibold">Custom Message</label>
        <textarea
          className="form-control"
          rows={7}
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Enter your message here..."
        />
        <small className="text-muted">This message will be included in the email sent to the employee.</small>
      </div>
    </BSModal>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function WorkAttendance() {
  const { user } = useAuth();
  const role = user?.roleKey || user?.role || '';
  const canAdmin = ['admin','manager','team_lead'].includes(role) || user?.userType === 'super_admin';
  const isEmployee = !['student'].includes(role);

  // ── Idle Detection ────────────────────────────────────────────────────────
  const IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours
  const IDLE_RESPONSE_MS = 5 * 60 * 1000; // 5 minutes
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  const [idleWarningShown, setIdleWarningShown] = useState(false);
  const [idleWarningTime, setIdleWarningTime] = useState(null);

  // Clock-out Correction Request
  const [dlgCorrection, setDlgCorrection] = useState(null); // { sessionId, logoutTime, reason }
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);
  const [correctionRequests, setCorrectionRequests] = useState([]);
  const [correctionPage, setCorrectionPage] = useState(1);
  const [correctionTotal, setCorrectionTotal] = useState(1);
  const [myCorrections, setMyCorrections] = useState([]);
  const [myCorrectionPage, setMyCorrectionPage] = useState(1);
  const [myCorrectionTotal, setMyCorrectionTotal] = useState(1);

  // ── Core State ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab]     = useState('my');
  const [alert, setAlert]             = useState({ show:false, msg:'', type:'success' });
  const showAlert = (msg, type='success') => setAlert({ show:true, msg, type });

  // Clock
  const [clockStatus, setClockStatus] = useState(null);
  const [clockNote, setClockNote]     = useState('');
  const [classCourseName, setClassCourseName] = useState('');
  const [clockLoading, setCL]         = useState(false);

  // My Attendance tab
  const [calMonth, setCalMonth]       = useState(new Date().getMonth()+1);
  const [calYear, setCalYear]         = useState(new Date().getFullYear());
  const [calDays, setCalDays]         = useState([]);
  const [stats, setStats]             = useState(null);
  const [myRecords, setMyRecords]     = useState([]);
  const [viewedUserId, setViewedUserId] = useState('');
  const [viewedUserActivities, setViewedUserActivities] = useState([]);
  const [userSearch, setUserSearch]   = useState('');
  const [expandedRecord, setExpandedRecord] = useState(null);
  const [activityFilterUser, setActivityFilterUser] = useState('');

  // Official Activities tab
  const [activities, setActivities]   = useState([]);
  const [actPage, setActPage]         = useState(1);
  const [actTotal, setActTotal]       = useState(1);
  const [actForm, setActForm]         = useState({ reason:'meeting', description:'', expectedMins:'' });
  const [actSubmitting, setActSub]    = useState(false);
  const [dlgApprove, setDlgApprove]   = useState(null);

  // Team Today tab
  const [teamToday, setTeamToday]     = useState([]);
  const [teamLoading, setTL]          = useState(false);

  // All Records tab
  const [records, setRecords]         = useState([]);
  const [recLoading, setRL]           = useState(false);
  const [filterUser, setFilterUser]   = useState('');
  const [startDate, setStartDate]     = useState(new Date(new Date().getFullYear(),new Date().getMonth(),1).toISOString().split('T')[0]);
  const [endDate, setEndDate]         = useState(todayISO());
  const [recPage, setRecPage]         = useState(1);
  const [recTotal, setRecTotal]       = useState(1);
  const [dlgAdjust, setDlgAdjust]     = useState(null);
  const [dlgManual, setDlgManual]     = useState(false);
  const [dlgFixLogout, setDlgFixLogout] = useState(null);
  const [dlgSelfFix, setDlgSelfFix]   = useState(null);
  const [selfFixing, setSelfFixing]   = useState(false);
  const [dlgAddSession, setDlgAddSession] = useState(null);
  const [dlgSendEmail, setDlgSendEmail] = useState(null);
  const [autoFillForm, setAutoFillForm] = useState({ year: new Date().getFullYear(), month: new Date().getMonth()+1, fillAbsentAs: 'leave' });
  const [autoFilling, setAutoFilling]   = useState(false);
  const [staffList, setStaffList]     = useState([]);

  // Policy tab
  const [policy, setPolicy]           = useState(null);
  const [polSaving, setPolSaving]     = useState(false);
  const [polForm, setPolForm]         = useState({});

  // Handle email approve/reject links on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const requestId = params.get('requestId');
    if (action && requestId && (action === 'approve' || action === 'reject')) {
      // Fetch the request details first
      api.get(`/training/work-attendance/clock-out-correction-requests`)
        .then(r => {
          const requests = r.data?.records || r.data?.data || [];
          const req = requests.find(x => x._id === requestId);
          if (!req) { showAlert('Request not found.', 'danger'); return; }
          if (action === 'approve') {
            const newTime = req.requestedLogoutTime ? new Date(req.requestedLogoutTime).toISOString().slice(0, 16) : '';
            const notes = 'Approved via email link';
            return api.patch(`/training/work-attendance/clock-out-correction-requests/${requestId}`, { status: 'approved', newLogoutTime: newTime, adminNotes: notes });
          } else {
            return api.patch(`/training/work-attendance/clock-out-correction-requests/${requestId}`, { status: 'rejected', adminNotes: 'Rejected via email link' });
          }
        })
        .then(() => {
          showAlert(`Correction request ${action === 'approve' ? 'approved' : 'rejected'}.`, 'success');
        })
        .catch(e => {
          showAlert(e.response?.data?.message || 'Action failed.', 'danger');
        })
        .finally(() => {
          // Clean URL
          window.history.replaceState({}, '', window.location.pathname);
        });
    }
  }, []);

  // Authorized Devices tab (admin)
  const [devices, setDevices]         = useState([]);
  const [devicesLoading, setDL2]      = useState(false);
  const [devicesFilter, setDevicesFilter] = useState('');
  const [deviceError, setDeviceError] = useState(null);
  const [pendingDeviceCount, setPendingDeviceCount] = useState(0);

  // Employee shifts tab (admin only)
  const [empConfigs, setEmpConfigs]   = useState([]);
  const [empCfgUser, setEmpCfgUser]   = useState('');
  const [empCfgForm, setEmpCfgForm]   = useState({});
  const [empCfgSaving, setEmpCfgSaving] = useState(false);

  // Bulk Entry tab (admin)
  const BULK_REASONS = ['Power cut','Network outage','Office closed','Weather / emergency','Early-out day','Holiday declared','Other'];
  const [bulkForm, setBulkForm]       = useState({ date: todayISO(), action: 'set', status: 'present', hours: '', reason: '', customReason: '' });
  const [bulkSelectAll, setBulkSelectAll] = useState(true);
  const [bulkSelected, setBulkSelected]   = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult]   = useState(null);

  // Monthly reports tab (admin)
  const [mrYear, setMrYear]           = useState(new Date().getFullYear());
  const [mrMonth, setMrMonth]         = useState(new Date().getMonth() + 1);
  const [mrReports, setMrReports]     = useState([]);
  const [mrLoading, setMrLoading]     = useState(false);
  const [mrGenerating, setMrGenerating] = useState(false);
  const [mrSendingAdmin, setMrSendingAdmin] = useState(false);
  const [mrDeletingId, setMrDeletingId] = useState('');
  const [mrOpen, setMrOpen]           = useState(null);
  const [mrLeaveTypes, setMrLeaveTypes] = useState([]);
  const [mrFilterEmployee, setMrFilterEmployee] = useState(''); // filter by employee
  const [mrCalendarDays, setMrCalendarDays] = useState([]); // full day-by-day for report detail
  const [mrDayRecords, setMrDayRecords] = useState({}); // { dateStr: attendanceLog }
  const [mrClockoutEntry, setMrClockoutEntry] = useState(null); // { date, clockOutTime, clockInTime, notes } per row
  const [mrSaving, setMrSaving] = useState(false);
  const [mrFinalizing, setMrFinalizing] = useState(false);
  const mrOperation = useRef(false);
  const mrReportRequest = useRef(0);

  const resetIdleTimer = useCallback(() => {
    setLastActivityTime(Date.now());
    setIdleWarningShown(false);
    setIdleWarningTime(null);
  }, []);

  useEffect(() => {
    const isClockedIn = clockStatus?.session?.loginTime && !clockStatus?.session?.logoutTime;
    if (!isEmployee || !isClockedIn) {
      setIdleWarningShown(false);
      setIdleWarningTime(null);
      return;
    }

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart', 'mousedown'];
    const handleActivity = () => resetIdleTimer();
    events.forEach(e => document.addEventListener(e, handleActivity, { passive: true }));

    const checkInterval = setInterval(() => {
      const idleMs = Date.now() - lastActivityTime;
      if (idleMs >= IDLE_TIMEOUT_MS && !idleWarningShown) {
        setIdleWarningShown(true);
        setIdleWarningTime(Date.now());
      }
      if (idleWarningShown && idleWarningTime && Date.now() - idleWarningTime >= IDLE_RESPONSE_MS) {
        handleClockWithIdleLogout();
      }
    }, 30000);

    return () => {
      events.forEach(e => document.removeEventListener(e, handleActivity));
      clearInterval(checkInterval);
    };
  }, [isEmployee, clockStatus, lastActivityTime, idleWarningShown, idleWarningTime, resetIdleTimer]);

  const handleClockWithIdleLogout = async () => {
    setIdleWarningShown(false);
    setIdleWarningTime(null);
    const logoutTime = new Date(lastActivityTime);
    setCL(true);
    try {
      const payload = {
        note: 'Auto clock-out: No activity detected for more than 2 hours',
        logoutTime: logoutTime.toISOString(),
        deviceFingerprint: getDeviceId(),
        hardwareHash: computeHardwareHash(),
        deviceInfo: collectDeviceInfo(),
        deviceName: getDeviceName(),
      };
      const r = await api.post('/training/work-attendance/clock-out', payload);
      if (r.data?.success !== false) {
        setDeviceError(null);
        showAlert(`Auto clocked out due to inactivity. Clock-out time: ${fmtTime(logoutTime)} on ${fmtDate(logoutTime)}`, 'warning');
        fetchClockStatus();
        fetchCalendar();
        if (activeTab === 'team' && canAdmin) fetchTeam();
      }
    } catch (e) {
      showAlert(e.response?.data?.message || 'Auto clock-out failed.', 'danger');
    }
    setCL(false);
  };

  // Dismiss idle warning - user clicked "I'm still working"
  const handleIdleDismiss = () => {
    resetIdleTimer();
  };

  // Clock out now from idle warning
  const handleIdleClockOut = () => {
    setIdleWarningShown(false);
    setIdleWarningTime(null);
    handleClock('clock-out');
  };

  // ── Clock status ─────────────────────────────────────────────────────────
  const fetchClockStatus = useCallback(() => {
    api.get('/training/work-attendance/clock-status')
      .then(r => setClockStatus(r.data?.data||r.data))
      .catch(()=>{});
  }, []);
  useEffect(() => {
    if (!isEmployee) return;
    fetchClockStatus();
    // Poll every 60 s so the "Going to Class" button activates automatically
    // when a class enters the scheduled window — no manual refresh needed.
    const id = setInterval(fetchClockStatus, 60000);
    return () => clearInterval(id);
  }, [fetchClockStatus, isEmployee]);

  // ── Staff list ───────────────────────────────────────────────────────────
  useEffect(() => {
    if(!canAdmin) return;
    api.get('/users',{params:{limit:1000}}).then(r=>setStaffList((r.data?.records||r.data?.data||[]).filter(u=>u.roleKey!=='student'))).catch(()=>{});
  }, [canAdmin]);

  // ── Calendar & stats ─────────────────────────────────────────────────────
  const fetchCalendar = useCallback(() => {
    // viewedUserId is set only when an admin picks a user from the master-
    // detail dropdown; backend ignores it for non-admin requesters.
    // calendar/stats/activities: these endpoints already default to the logged-in
    // user when no userId is supplied, so the original scope is correct.
    const scope = viewedUserId ? { userId: viewedUserId } : {};
    // For the records list (GET /): admin with no userId gets ALL employees'
    // records. Use userId=self so the backend scopes to req.userId (the admin's
    // own records) without affecting the All Records tab which sends no userId.
    const recScope = viewedUserId
      ? { userId: viewedUserId }
      : canAdmin ? { userId: 'self' } : {};
    api.get('/training/work-attendance/calendar',{params:{year:calYear,month:calMonth,...scope}})
      .then(r=>setCalDays(r.data?.data||[])).catch(()=>{});
    api.get('/training/work-attendance/stats',{params:{month:calMonth,year:calYear,...scope}})
      .then(r=>setStats(r.data?.data||r.data)).catch(()=>{});
    api.get('/training/work-attendance',{params:{page:1,limit:31,startDate:`${calYear}-${String(calMonth).padStart(2,'0')}-01`,endDate:`${calYear}-${String(calMonth).padStart(2,'0')}-31`,...recScope}})
      .then(r=>setMyRecords(r.data?.records||r.data?.data||[])).catch(()=>{});
    api.get('/training/work-attendance/activities',{params:{limit:20,...scope}})
      .then(r=>setViewedUserActivities(r.data?.records||r.data?.data||[])).catch(()=>{});
    // Always load policy so weekend days are known for the full-month grid.
    if (!policy) api.get('/training/work-attendance/policy').then(r=>{ const p=r.data?.data||{}; setPolicy(p); setPolForm(p); }).catch(()=>{});
  }, [calMonth, calYear, viewedUserId, canAdmin]);
  useEffect(() => { if(activeTab==='my') fetchCalendar(); }, [activeTab, fetchCalendar]);

  // ── Activities ───────────────────────────────────────────────────────────
  const fetchActivities = useCallback(() => {
    const scope = activityFilterUser ? { userId: activityFilterUser } : {};
    api.get('/training/work-attendance/activities',{params:{page:actPage,limit:10,...scope}})
      .then(r=>{ setActivities(r.data?.records||r.data?.data||[]); setActTotal(r.data?.totalPages||1); }).catch(()=>{});
  }, [actPage, activityFilterUser]);
  useEffect(() => { if(activeTab==='activities') fetchActivities(); }, [activeTab, fetchActivities]);

  // ── Team Today ───────────────────────────────────────────────────────────
  // Backend wraps arrays under `records` (via success() helper), not `data` —
  // that was why this tab showed "No team data available." even when the API
  // returned rows. Read both for safety.
  const fetchTeam = useCallback(() => {
    setTL(true);
    api.get('/training/work-attendance/team/today')
      .then(r => setTeamToday(r.data?.records || r.data?.data || []))
      .catch(() => {})
      .finally(() => setTL(false));
  }, []);
  useEffect(() => { if(activeTab==='team'&&canAdmin) fetchTeam(); }, [activeTab, fetchTeam, canAdmin]);

  // While the Team Today tab is open, auto-refresh every 30s so cards reflect
  // clock-in/out and lunch activity from teammates without requiring a click.
  useEffect(() => {
    if (activeTab !== 'team' || !canAdmin) return;
    const id = setInterval(fetchTeam, 30000);
    return () => clearInterval(id);
  }, [activeTab, fetchTeam, canAdmin]);

  // ── All Records ──────────────────────────────────────────────────────────
  const fetchRecords = useCallback(() => {
    setRL(true);
    const params = { page:recPage, limit:20, startDate, endDate };
    if(filterUser) params.userId = filterUser;
    api.get('/training/work-attendance',{params})
      .then(r=>{ setRecords(r.data?.records||r.data?.data||[]); setRecTotal(r.data?.totalPages||1); }).catch(()=>{}).finally(()=>setRL(false));
  }, [recPage, startDate, endDate, filterUser]);
  useEffect(() => { if(activeTab==='records'&&canAdmin) fetchRecords(); }, [activeTab, fetchRecords, canAdmin]);

  // ── Clock-Out Correction Requests (admin) ─────────────────────────────────
  const fetchCorrections = useCallback(() => {
    setRL(true);
    api.get('/training/work-attendance/clock-out-correction-requests', { params: { page: correctionPage, limit: 10, status: 'pending' } })
      .then(r => {
        const records = r.data?.records || r.data?.data || [];
        console.log('[FetchCorrections] Received data:', records);
        records.forEach(req => {
          console.log('[FetchCorrections] Request:', req._id, 'lastActivityTime:', req.lastActivityTime, 'lastInteractionTime:', req.lastInteractionTime);
        });
        setCorrectionRequests(records);
        setCorrectionTotal(r.data?.totalPages || 1);
      })
      .catch(() => {})
      .finally(() => setRL(false));
  }, [correctionPage]);
  useEffect(() => { if(activeTab==='corrections'&&canAdmin) fetchCorrections(); }, [activeTab, fetchCorrections, canAdmin]);

  // ── My Correction Requests (employee) ─────────────────────────────────────
  const fetchMyCorrections = useCallback(() => {
    setRL(true);
    api.get('/training/work-attendance/my-clock-out-correction-requests', { params: { page: myCorrectionPage, limit: 10 } })
      .then(r => {
        const records = r.data?.records || r.data?.data || [];
        console.log('[FetchMyCorrections] Received data:', records);
        records.forEach(req => {
          console.log('[FetchMyCorrections] Request:', req._id, 'lastActivityTime:', req.lastActivityTime, 'lastInteractionTime:', req.lastInteractionTime);
        });
        setMyCorrections(records);
        setMyCorrectionTotal(r.data?.totalPages || 1);
      })
      .catch(() => {})
      .finally(() => setRL(false));
  }, [myCorrectionPage]);
  useEffect(() => { if(activeTab==='my_corrections'&&!canAdmin) fetchMyCorrections(); }, [activeTab, fetchMyCorrections, canAdmin]);

  // ── Policy ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if(activeTab==='policy'&&canAdmin)
      api.get('/training/work-attendance/policy').then(r=>{ const p=r.data?.data||{}; setPolicy(p); setPolForm(p); }).catch(()=>{});
  }, [activeTab, canAdmin]);

  // ── Employee Shifts (admin) ──────────────────────────────────────────────
  const fetchEmpConfigs = useCallback(() => {
    api.get('/training/work-attendance/employee-configs')
      .then(r => setEmpConfigs(r.data?.data || r.data?.records || []))
      .catch(() => {});
  }, []);
  useEffect(() => { if(activeTab==='employee_shifts'&&canAdmin) fetchEmpConfigs(); }, [activeTab, canAdmin, fetchEmpConfigs]);

  // When a user is selected in the editor, prefill their current values (or blanks).
  useEffect(() => {
    if (!empCfgUser) { setEmpCfgForm({}); return; }
    const existing = empConfigs.find(c => String(c.employee?._id || c.employee) === String(empCfgUser));
    setEmpCfgForm({
      shiftStart:          existing?.shiftStart          || '',
      shiftEnd:            existing?.shiftEnd            || '',
      requiredHoursPerDay: existing?.requiredHoursPerDay ?? '',
      gracePeriodMinutes:  existing?.gracePeriodMinutes  ?? '',
      breakTimeMinutes:    existing?.breakTimeMinutes    ?? '',
      monthlySalary:       existing?.monthlySalary       ?? '',
      joiningDate:         existing?.joiningDate ? new Date(existing.joiningDate).toISOString().split('T')[0] : '',
      lastWorkingDate:     existing?.lastWorkingDate ? new Date(existing.lastWorkingDate).toISOString().split('T')[0] : '',
      probationMonths:     existing?.probationMonths     ?? '',
      notes:               existing?.notes               || '',
      deviceLock:          existing?.deviceLock          ?? false,
    });
  }, [empCfgUser, empConfigs]);

  const saveEmpConfig = async () => {
    if (!empCfgUser) return;
    setEmpCfgSaving(true);
    try {
      // Empty strings → tell backend to clear (fall back to policy default)
      const payload = {};
      ['shiftStart','shiftEnd','requiredHoursPerDay','gracePeriodMinutes','breakTimeMinutes','monthlySalary','joiningDate','lastWorkingDate','probationMonths','notes'].forEach(k => {
        const v = empCfgForm[k];
        payload[k] = (v === '' || v === null || v === undefined) ? '' : v;
      });
      payload.deviceLock = !!empCfgForm.deviceLock;
      const response = await api.put(`/training/work-attendance/employee-config/${empCfgUser}`, payload);
      const result = response.data?.data || response.data || {};
      for (const key of ['joiningDate', 'lastWorkingDate']) {
        if (payload[key] && (!result.config?.[key] || new Date(result.config[key]).toISOString().slice(0, 10) !== payload[key])) {
          throw new Error('The server did not confirm the employment dates. Check the deployed WorkAttendance model and restart the backend.');
        }
      }
      showAlert(response.data?.message || 'Shift configuration saved.', result.reportRefreshPending ? 'warning' : 'success');
      fetchEmpConfigs();
      fetchMrReports();
      if (String(mrOpen?.employee?._id || mrOpen?.employee || '') === String(empCfgUser)) await openReport(mrOpen._id);
    } catch (e) {
      showAlert(e.response?.data?.message || e.message || 'Save failed.', 'danger');
    }
    setEmpCfgSaving(false);
  };

  const removeEmpConfig = async () => {
    if (!empCfgUser) return;
    if (!window.confirm('Remove the per-user override? This user will revert to the tenant policy defaults.')) return;
    setEmpCfgSaving(true);
    try {
      await api.delete(`/training/work-attendance/employee-config/${empCfgUser}`);
      showAlert('Override removed.', 'success');
      setEmpCfgForm({});
      fetchEmpConfigs();
    } catch (e) {
      showAlert(e.response?.data?.message || 'Remove failed.', 'danger');
    }
    setEmpCfgSaving(false);
  };

  // ── Authorized Devices ───────────────────────────────────────────────────
  const fetchDevices = useCallback(() => {
    if (!canAdmin) return;
    setDL2(true);
    const params = {};
    if (devicesFilter) params.status = devicesFilter;
    api.get('/training/work-attendance/devices', { params })
      .then(r => setDevices(r.data?.records || r.data?.data || []))
      .catch(() => {})
      .finally(() => setDL2(false));
  }, [canAdmin, devicesFilter]);
  useEffect(() => { if (activeTab === 'devices' && canAdmin) fetchDevices(); }, [activeTab, canAdmin, fetchDevices]);

  // Keep pending badge count fresh — poll once on mount and again whenever a
  // device is approved/rejected (fetchDevices resets the filter back to 'all',
  // so we use a separate lightweight call that never affects visible state).
  const refreshPendingCount = useCallback(() => {
    if (!canAdmin) return;
    api.get('/training/work-attendance/devices', { params: { status: 'pending' } })
      .then(r => setPendingDeviceCount((r.data?.records || r.data?.data || []).length))
      .catch(() => {});
  }, [canAdmin]);
  useEffect(() => { refreshPendingCount(); }, [refreshPendingCount]);

  const approveDevice = async (id) => {
    try {
      await api.patch(`/training/work-attendance/devices/${id}/approve`);
      showAlert('Device approved. The employee can now clock in from this device.', 'success');
      fetchDevices();
      refreshPendingCount();
    } catch (e) { showAlert(e.response?.data?.message || 'Failed.', 'danger'); }
  };
  const rejectDevice = async (id) => {
    try {
      await api.patch(`/training/work-attendance/devices/${id}/reject`);
      showAlert('Device rejected.', 'success');
      fetchDevices();
      refreshPendingCount();
    } catch (e) { showAlert(e.response?.data?.message || 'Failed.', 'danger'); }
  };
  const deleteDevice = async (id) => {
    if (!window.confirm('Remove this device? The employee will need to re-register from that system.')) return;
    try {
      await api.delete(`/training/work-attendance/devices/${id}`);
      showAlert('Device removed.', 'success');
      fetchDevices();
    } catch (e) { showAlert(e.response?.data?.message || 'Failed.', 'danger'); }
  };

  // ── Clock-Out Correction Review (admin) ──────────────────────────────────
  const reviewCorrection = async (requestId, status) => {
    if (status === 'approved') {
      const req = correctionRequests.find(r => r._id === requestId);
      if (!req) return;
      const requested = req.requestedLogoutTime ? new Date(req.requestedLogoutTime) : null;
      const localDefault = requested ? new Date(requested.getTime() - requested.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '';
      const newTime = prompt(`Enter the corrected clock-out time (original: ${fmtTime(req.originalLogoutTime)}, requested: ${fmtTime(req.requestedLogoutTime)}, last interaction: ${fmtTime(req.lastActivityTime)}):`, localDefault);
      if (!newTime) return;
      const notes = prompt('Add admin notes (optional):', '');
      try {
        await api.patch(`/training/work-attendance/clock-out-correction-requests/${requestId}`, { status: 'approved', newLogoutTime: new Date(newTime).toISOString(), adminNotes: notes || '' });
        showAlert('Correction approved. Clock-out time updated and employee notified.', 'success');
        fetchCorrections();
        fetchRecords();
        fetchCalendar();
        fetchClockStatus();
        fetchMrReports();
      } catch (e) { showAlert(e.response?.data?.message || 'Failed.', 'danger'); }
    } else {
      const notes = prompt('Reason for rejection (optional):', '');
      try {
        await api.patch(`/training/work-attendance/clock-out-correction-requests/${requestId}`, { status: 'rejected', adminNotes: notes || '' });
        showAlert('Correction request rejected. Employee notified.', 'success');
        fetchCorrections();
      } catch (e) { showAlert(e.response?.data?.message || 'Failed.', 'danger'); }
    }
  };

  // ── Monthly Reports ──────────────────────────────────────────────────────
  const fetchMrReports = useCallback(() => {
    setMrLoading(true);
    api.get('/training/work-attendance/reports', { params: { year: mrYear, month: mrMonth, limit: 200 } })
      .then(r => setMrReports(r.data?.records || r.data?.data || []))
      .catch(() => {})
      .finally(() => setMrLoading(false));
  }, [mrYear, mrMonth]);

  // Settings-driven list of leave types (for the reclassification dropdown).
  useEffect(() => {
    if (activeTab !== 'monthly_reports' || !canAdmin) return;
    api.get('/training/settings')
      .then(r => {
        const lp = r.data?.data?.leavePolicy || r.data?.leavePolicy || [];
        setMrLeaveTypes(lp);
      })
      .catch(() => setMrLeaveTypes([]));
  }, [activeTab, canAdmin]);

  useEffect(() => {
    if (activeTab === 'monthly_reports' && canAdmin) fetchMrReports();
  }, [activeTab, canAdmin, fetchMrReports]);

  const generateMonthlyReports = async (userId = null) => {
    setMrGenerating(true);
    try {
      const body = { year: mrYear, month: mrMonth };
      if (userId) body.userId = userId;
      const r = await api.post('/training/work-attendance/reports/generate', body);
      showAlert(r.data?.message || 'Reports generated.', 'success');
      fetchMrReports();
    } catch (e) {
      showAlert(e.response?.data?.message || 'Generation failed.', 'danger');
    }
    setMrGenerating(false);
  };

  const deleteMonthlyReport = async (report) => {
    const employeeName = `${report?.employee?.firstName || ''} ${report?.employee?.lastName || ''}`.trim() || 'this employee';
    if (!window.confirm(`Remove ${employeeName}'s attendance report for ${MONTHS[mrMonth - 1]} ${mrYear}? Daily clock-in and clock-out records will not be deleted.`)) return;
    setMrDeletingId(report._id);
    try {
      const response = await api.delete(`/training/work-attendance/reports/${report._id}`);
      if (String(mrOpen?._id || '') === String(report._id)) setMrOpen(null);
      if (String(mrFilterEmployee || '') === String(report.employee?._id || report.employee || '')) setMrFilterEmployee('');
      showAlert(response.data?.message || 'Monthly attendance report removed.', 'success');
      fetchMrReports();
    } catch (error) {
      showAlert(error.response?.data?.message || 'Could not remove the monthly attendance report.', 'danger');
    } finally {
      setMrDeletingId('');
    }
  };

  const buildMonthlyReportPDF = () => {
    const filtered = mrReports.filter((report) => {
      const matchesEmployee = !mrFilterEmployee
        || String(report.employee?._id || report.employee) === mrFilterEmployee;
      return matchesEmployee && Number(report.presentDays || 0) > 0;
    });
    if (filtered.length === 0) {
      showAlert('No employees with present attendance are available to download.', 'warning');
      return null;
    }
    const doc = new jsPDF();

    // ─── Header: Company Name + Logo ────────────────────────────────────────
    const companyName = (() => {
      try {
        const cached = JSON.parse(localStorage.getItem('tenant_branding') || '{}');
        return cached.companyName || 'Top Skilled Academy';
      } catch { return 'Top Skilled Academy'; }
    })();
    const companyTagline = (() => {
      try {
        const cached = JSON.parse(localStorage.getItem('tenant_branding') || '{}');
        return cached.tagline || 'Empowering Skills · Building Futures';
      } catch { return 'Empowering Skills · Building Futures'; }
    })();
    const logoUrl = (() => {
      try {
        const cached = JSON.parse(localStorage.getItem('tenant_branding') || '{}');
        return cached.logoUrl || null;
      } catch { return null; }
    })();

    // Bordered header box
    doc.setDrawColor(41, 128, 185);
    doc.setLineWidth(0.8);
    doc.rect(10, 8, 190, 30);

    // Logo on the left side of header (if available)
    if (logoUrl) {
      try {
        doc.addImage(logoUrl, 'PNG', 13, 12, 22, 22);
      } catch (e) {
        console.warn('Could not add logo to PDF:', e.message);
      }
    }

    // Company name + tagline (next to logo)
    doc.setFontSize(16);
    doc.setTextColor(41, 128, 185);
    doc.setFont('helvetica', 'bold');
    doc.text(companyName, logoUrl ? 40 : 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.setFont('helvetica', 'normal');
    doc.text(companyTagline, logoUrl ? 40 : 14, 24);

    // Right side: report month/year
    doc.setFontSize(13);
    doc.setTextColor(41, 128, 185);
    doc.setFont('helvetica', 'bold');
    doc.text(`Monthly Attendance Report`, 200, 16, { align: 'right' });
    doc.setFontSize(11);
    doc.setTextColor(60);
    doc.setFont('helvetica', 'normal');
    doc.text(`${MONTHS[mrMonth - 1]} ${mrYear}`, 200, 22, { align: 'right' });
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, 200, 28, { align: 'right' });

    // Divider line under header
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(10, 42, 200, 42);

    const formatHours = (value) => `${Number(value || 0).toFixed(1)}h`;

    const tableData = filtered.map((r, index) => [
      index + 1,
      r.employee?.firstName && r.employee?.lastName
        ? `${r.employee.firstName} ${r.employee.lastName}`
        : 'Unknown',
      r.workingDays ?? new Date(r.year, r.month, 0).getDate(),
      Number(r.presentDays || 0),
      Number(r.absentDays ?? r.totalLeaveDays ?? 0),
      formatHours(Number(r.lateLoginMinutes || 0) / 60),
      formatHours(r.permissionHoursUsed),
      formatHours(r.excessPermissionHours),
    ]);

    autoTable(doc, {
      head: [['S.No', 'Employee Name', 'Days in Month', 'Present Days', 'Leave / Absent Days', 'Late Login Hours', 'Permission Hours', 'Excess Permission Hours']],
      body: tableData,
      startY: 46,
      styles: { fontSize: 8, lineColor: [180, 180, 180], lineWidth: 0.1 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', lineWidth: 0.3 },
      bodyStyles: { lineWidth: 0.1 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      tableLineColor: [180, 180, 180],
      tableLineWidth: 0.3,
    });

    // Footer with company name
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(`${companyName} · Page ${i} of ${pageCount}`, 105, 290, { align: 'center' });
    }

    return doc;
  };

  const downloadMonthlyReportPDF = () => {
    const doc = buildMonthlyReportPDF();
    if (!doc) return;
    doc.save(`Attendance_Report_${MONTHS[mrMonth - 1]}_${mrYear}.pdf`);
    showAlert('PDF downloaded.', 'success');
  };

  const sendMonthlyReportToAdmins = async () => {
    const doc = buildMonthlyReportPDF();
    if (!doc) return;
    setMrSendingAdmin(true);
    try {
      const dataUri = doc.output('datauristring');
      const pdfBase64 = dataUri.slice(dataUri.indexOf(',') + 1);
      const response = await api.post('/training/work-attendance/reports/send-admin', {
        year: mrYear,
        month: mrMonth,
        pdfBase64,
      });
      showAlert(response.data?.message || 'Attendance report sent to administrators.', 'success');
    } catch (error) {
      showAlert(error.response?.data?.message || 'Could not send the attendance report.', 'danger');
    } finally {
      setMrSendingAdmin(false);
    }
  };

  const sendMonthlyReportEmail = async (reportId) => {
    try {
      const r = await api.post(`/training/work-attendance/reports/${reportId}/send-email`);
      showAlert(r.data?.message || 'Email sent successfully.', 'success');
    } catch (e) {
      showAlert(e.response?.data?.message || 'Failed to send email.', 'danger');
    }
  };

  const openReport = async (id, { throwOnError = false, expectedCorrection } = {}) => {
    const requestId = ++mrReportRequest.current;
    try {
      const r = await api.get('/training/work-attendance/reports/' + id);
      if (r.data?.success === false) throw new Error(r.data.message || 'Failed to load report.');
      const report = r.data?.data || r.data;
      let days, recMap;
      if (report.status === 'finalized' && report.dailySnapshot?.length) {
        days = report.dailySnapshot;
        recMap = Object.fromEntries(days.filter(day => day.record).map(day => [day.date, day.record]));
      } else {
        const empId = report.employee?._id || report.employee;
        if (!empId || !report.year || !report.month) throw new Error('Report employee or period is missing.');
        const prefix = report.year + '-' + String(report.month).padStart(2, '0');
        const [calR, recR] = await Promise.all([
          api.get('/training/work-attendance/calendar', { params: { userId: empId, year: report.year, month: report.month } }),
          api.get('/training/work-attendance', { params: {
            userId: empId, startDate: prefix + '-01',
            endDate: prefix + '-' + new Date(report.year, report.month, 0).getDate(), limit: 31,
          }}),
        ]);
        if (calR.data?.success === false || recR.data?.success === false) throw new Error('Failed to refresh attendance rows.');
        days = calR.data?.records || calR.data?.data || [];
        const today = new Date().toISOString().slice(0, 10);
        const joined = report.joiningDate ? new Date(report.joiningDate).toISOString().slice(0, 10) : '';
        const last = report.lastWorkingDate ? new Date(report.lastWorkingDate).toISOString().slice(0, 10) : '';
        days = days.map(day => day.date <= today && ((joined && day.date < joined) || (last && day.date > last))
          ? { ...day, status: 'absent', leaveLabel: null, totalMins: 0, isWeekend: false } : day);
        const recs = recR.data?.records || recR.data?.data || [];
        recMap = Object.fromEntries(recs.map(rec => [new Date(rec.date).toISOString().slice(0, 10), rec]));
      }
      if (requestId !== mrReportRequest.current) return false;
      if (expectedCorrection) {
        const row = recMap[expectedCorrection.date];
        const sameTime = (a, b) => !b || (a && new Date(a).getTime() === new Date(b).getTime());
        if (!row || (expectedCorrection.attendanceStatus !== 'auto' && row.manualAttendanceStatus !== expectedCorrection.attendanceStatus) ||
            !sameTime(row.firstLogin?.time, expectedCorrection.firstLogin) || !sameTime(row.lastLogout?.time, expectedCorrection.lastLogout)) {
          throw new Error('The report returned older attendance data. Refresh the report, or reopen it if finalized.');
        }
      }
      setMrOpen(report);
      setMrCalendarDays(days);
      setMrDayRecords(recMap);
      return true;
    } catch (e) {
      if (throwOnError) throw e;
      if (requestId === mrReportRequest.current) showAlert(e.response?.data?.message || e.message || 'Failed to load report.', 'danger');
      return false;
    }
  };

  const handleSetClockout = async (entry) => {
    if (!mrOpen || mrOperation.current) return;
    const timeChanged = (entry.clockInTime || '') !== (entry.originalClockInTime || '') ||
      (entry.clockOutTime || '') !== (entry.originalClockOutTime || '');
    const statusOnly = !timeChanged;
    if (timeChanged && ((entry.originalClockInTime && !entry.clockInTime) || (entry.originalClockOutTime && !entry.clockOutTime))) {
      return showAlert('Enter the new time instead of clearing an existing clock time.', 'warning');
    }
    mrOperation.current = true;
    setMrSaving(true);
    let attendanceSaved = false;
    try {
      const empId = mrOpen.employee?._id || mrOpen.employee;
      const r = await api.patch('/training/work-attendance/set-clockout', {
        employeeId: empId, date: entry.date, statusOnly,
        ...(!statusOnly ? {
          clockOutTime: entry.clockOutTime || undefined,
          clockInTime: entry.clockInTime || undefined,
        } : {}),
        attendanceStatus: entry.attendanceStatus || 'auto',
        notes: entry.notes || 'Admin corrected attendance',
      });
      if (r.data?.success === false) throw new Error(r.data.message || 'Correction was not saved.');
      const saved = r.data?.data?.data || r.data?.data;
      if (!saved || (entry.attendanceStatus && entry.attendanceStatus !== 'auto' && saved.manualAttendanceStatus !== entry.attendanceStatus)) {
        console.error('[attendance-correction] confirmation mismatch', {
          httpStatus: r.status,
          correctionVersion: r.data?.correctionVersion || r.data?.data?.correctionVersion || 'missing',
          responseKeys: Object.keys(r.data || {}),
          dataKeys: Object.keys(r.data?.data || {}),
          requestedStatus: entry.attendanceStatus || 'auto',
          returnedStatus: saved?.status,
          returnedManualStatus: saved?.manualAttendanceStatus,
        });
        throw new Error('The server did not confirm the selected attendance status. Check that the latest backend model and routes are deployed.');
      }
      attendanceSaved = true;
      ++mrReportRequest.current;
      setMrDayRecords(previous => ({ ...previous, [entry.date]: saved }));
      setMrCalendarDays(previous => previous.map(day => day.date === entry.date ? {
        ...day, status: saved.status, totalMins: saved.totalWorkedMinutes || 0,
        leaveLabel: saved.manualAttendanceStatus ? null : day.leaveLabel,
      } : day));
      setMrClockoutEntry(null);
      const refreshed = await openReport(mrOpen._id, { throwOnError: true, expectedCorrection: {
        date: entry.date, attendanceStatus: entry.attendanceStatus || 'auto',
        firstLogin: saved.firstLogin?.time, lastLogout: saved.lastLogout?.time,
      } });
      fetchMrReports();
      fetchCalendar();
      fetchRecords();
      fetchTeam();
      showAlert(refreshed ? 'Attendance correction saved and report refreshed.' : 'Attendance saved. Refresh the report to see the latest totals.',
        refreshed ? 'success' : 'warning');
    } catch (e) {
      showAlert(attendanceSaved
        ? 'Attendance was saved, but the report could not reload. Refresh the report; do not submit the same correction again.'
        : e.response?.data?.message || e.message || 'Failed to save attendance.', attendanceSaved ? 'warning' : 'danger');
    } finally {
      mrOperation.current = false;
      setMrSaving(false);
    }
  };

  const finalizeReport = async () => {
    if (!mrOpen || mrOperation.current) return;
    if (mrClockoutEntry) return showAlert('Save the attendance correction using the green check before finalizing.', 'warning');
    if (!window.confirm('Finalize this report? It will be locked from edits until reopened.')) return;
    mrOperation.current = true;
    setMrFinalizing(true);
    try {
      await api.patch(`/training/work-attendance/reports/${mrOpen._id}/finalize`);
      await openReport(mrOpen._id);
      fetchMrReports();
      showAlert('Report finalized.', 'success');
    } catch (e) {
      showAlert(e.response?.data?.message || 'Finalize failed.', 'danger');
    } finally {
      mrOperation.current = false;
      setMrFinalizing(false);
    }
  };

  const reopenReport = async () => {
    if (!mrOpen || mrOperation.current) return;
    mrOperation.current = true;
    setMrFinalizing(true);
    try {
      await api.patch(`/training/work-attendance/reports/${mrOpen._id}/reopen`);
      await openReport(mrOpen._id);
      fetchMrReports();
      showAlert('Report reopened.', 'success');
    } catch (e) {
      showAlert(e.response?.data?.message || 'Reopen failed.', 'danger');
    } finally {
      mrOperation.current = false;
      setMrFinalizing(false);
    }
  };

  // ── Clock / Lunch actions ────────────────────────────────────────────────
  // action ∈ 'clock-in' | 'clock-out' | 'lunch-start' | 'lunch-end'
  const handleClock = async (action) => {
    setCL(true);
    try {
      const needsNote = action === 'clock-in' || action === 'clock-out';
      const payload   = needsNote ? { note: clockNote } : {};
      if (action === 'class-start' && classCourseName.trim()) {
        payload.courseName = classCourseName.trim();
      }
      // Always attach device identity — backend enforces it only when device
      // locking is enabled, but sending on all actions is harmless and gives
      // the server a hardware trail for audit.
      payload.deviceFingerprint = getDeviceId();
      payload.hardwareHash      = computeHardwareHash();
      payload.deviceInfo        = collectDeviceInfo();
      payload.deviceName        = getDeviceName();
      const r = await api.post(`/training/work-attendance/${action}`, payload);
      if (r.data?.success !== false) {
        setDeviceError(null);
        const firstName = user?.firstName || '';
        // Daily-rotating greeting on clock-in / lunch-start so the toast
        // feels less robotic. Generic strings on clock-out / lunch-end /
        // class transitions.
        const sched = r.data?.data?.scheduled;
        const classLabel = sched ? (sched.batchLabel || sched.courseLabel || 'class') : '';
        const msg = {
          'clock-in':    pickDaily(HAPPY_MESSAGES, firstName),
          'clock-out':   `Clocked out — see you tomorrow${firstName ? `, ${firstName}` : ''}!`,
          'lunch-start': pickDaily(LUNCH_MESSAGES, firstName),
          'lunch-end':   `Welcome back${firstName ? `, ${firstName}` : ''}! Back to it.`,
          'class-start': `Class started${classLabel ? `: ${classLabel}` : ''}. Make it count! 📚`,
          'class-end':   `Class ended. Nice work${firstName ? `, ${firstName}` : ''}!`,
        }[action] || 'Done.';
        showAlert(msg, 'success');
        if (needsNote) setClockNote('');
        // Always refresh clock status + calendar so the top panel AND the records
        // table both reflect the latest session / first-in / last-out / lunch totals.
        fetchClockStatus();
        fetchCalendar();
        // If admin is watching the Team tab, refresh its cards too.
        if (activeTab === 'team' && canAdmin) fetchTeam();
      } else {
        showAlert(r.data?.message || 'Failed.', 'danger');
      }
    } catch (e) {
      const status = e.response?.status;
      const msg    = e.response?.data?.message || '';
      const errCode = e.response?.data?.code || '';
      // Surface device-lock errors as a persistent banner on the clock panel
      // so the user understands WHY the button didn't work without reading
      // only the toast (which disappears).
      if (status === 403 && action === 'clock-in') {
        if (msg.includes('registration request has been submitted')) setDeviceError('unregistered');
        else if (msg.includes('rejected by your admin'))             setDeviceError('rejected');
        else if (msg.includes('authorization is pending'))           setDeviceError('pending');
        else if (msg.includes('hardware mismatch'))                  setDeviceError('mismatch');
      }
      if (errCode === 'CORRECTION_PENDING' && action === 'clock-in') {
        setDeviceError('correction_pending');
      }
      showAlert(msg || 'Failed.', 'danger');
    }
    setCL(false);
  };

  // ── Submit Activity ──────────────────────────────────────────────────────
  const submitActivity = async () => {
    if(!actForm.description||!actForm.expectedMins) return showAlert('Fill all fields.','warning');
    const mins = parseInt(actForm.expectedMins, 10);
    if (!mins || mins <= 0) return showAlert('Expected duration must be a positive number of minutes.','warning');
    setActSub(true);
    try {
      // Backend expects `expectedDurationMinutes`, not `expectedMins`.
      const payload = {
        reason: actForm.reason,
        description: actForm.description,
        expectedDurationMinutes: mins,
      };
      const r = await api.post('/training/work-attendance/activities', payload);
      if(r.data?.success!==false){ showAlert('Activity submitted.','success'); setActForm({ reason:'meeting', description:'', expectedMins:'' }); fetchActivities(); }
      else showAlert(r.data?.message||'Failed.','danger');
    } catch(e){ showAlert(e.response?.data?.message||'Failed.','danger'); }
    setActSub(false);
  };

  // ── Bulk Entry ───────────────────────────────────────────────────────────
  const submitBulk = async () => {
    const effectiveReason = bulkForm.reason === 'Other' ? bulkForm.customReason.trim() : bulkForm.reason;
    if (!effectiveReason) return showAlert('Please enter a reason.', 'warning');
    if ((bulkForm.action === 'set' || bulkForm.action === 'add') && !bulkForm.hours)
      return showAlert('Enter hours to apply.', 'warning');
    setBulkLoading(true); setBulkResult(null);
    try {
      const hrs = parseFloat(bulkForm.hours || '0');
      const payload = {
        date:   bulkForm.date,
        action: bulkForm.action,
        status: bulkForm.status || 'present',
        reason: effectiveReason,
        ...(bulkForm.action !== 'holiday' && bulkForm.action !== 'absent'
          ? { totalWorkedMinutes: Math.round(hrs * 60) } : {}),
        ...(!bulkSelectAll && bulkSelected.length ? { employeeIds: bulkSelected } : {}),
      };
      const r = await api.post('/training/work-attendance/bulk-admin', payload);
      if (r.data?.success !== false) {
        setBulkResult(r.data?.data || {});
        showAlert(r.data?.message || 'Done.', 'success');
        fetchCalendar();
        fetchRecords();
        fetchTeam();
        fetchMrReports();
        if (mrOpen?._id) await openReport(mrOpen._id);
      } else showAlert(r.data?.message || 'Failed.', 'danger');
    } catch(e) { showAlert(e.response?.data?.message || 'Failed.', 'danger'); }
    setBulkLoading(false);
  };

  const fixLogout = async ({ sessionId, logoutTime, warningNote }) => {
    try {
      const r = await api.post('/training/work-attendance/admin-clock-out-correction-request', { sessionId, requestedLogoutTime: logoutTime, reason: warningNote || 'Admin correction' });
      if (r.data?.success !== false) {
        showAlert('Correction request created. Employee will be notified to approve or reject.', 'success');
        fetchTeam();
      } else showAlert(r.data?.message || 'Failed.', 'danger');
    } catch(e) { showAlert(e.response?.data?.message || 'Failed.', 'danger'); }
  };

  const submitSelfFix = async () => {
    if (!dlgSelfFix?.logoutTime) return showAlert('Please enter your actual logout time.', 'warning');
    setSelfFixing(true);
    try {
      const staleSession = clockStatus?.staleSessionId;
      if (!staleSession) return showAlert('No stale session found.', 'danger');
      const r = await api.post('/training/work-attendance/clock-out-correction-request', {
        sessionId: staleSession,
        requestedLogoutTime: dlgSelfFix.logoutTime,
        reason: dlgSelfFix.reason || 'Forgot to clock out',
      });
      if (r.data?.success !== false) {
        showAlert('Your correction request has been submitted. Your admin will review it.', 'success');
        setDlgSelfFix(null);
        fetchClockStatus();
        fetchCalendar();
      } else showAlert(r.data?.message || 'Failed.', 'danger');
    } catch(e) { showAlert(e.response?.data?.message || 'Failed.', 'danger'); }
    setSelfFixing(false);
  };

  // Submit clock-out correction request
  const submitCorrection = async () => {
    if (!dlgCorrection?.requestedTime || !dlgCorrection?.reason) return showAlert('Please fill in both the corrected time and reason.', 'warning');
    setCorrectionSubmitting(true);
    try {
      const r = await api.post('/training/work-attendance/clock-out-correction-request', {
        sessionId: dlgCorrection.sessionId,
        requestedLogoutTime: dlgCorrection.requestedTime,
        reason: dlgCorrection.reason,
      });
      if (r.data?.success !== false) {
        showAlert('Your correction request has been submitted. Your admin will review it.', 'success');
        setDlgCorrection(null);
      } else showAlert(r.data?.message || 'Failed.', 'danger');
    } catch(e) { showAlert(e.response?.data?.message || 'Failed.', 'danger'); }
    setCorrectionSubmitting(false);
  };

  const submitAddSession = async () => {
    if (!dlgAddSession?.employeeId || !dlgAddSession?.loginTime) return showAlert('Employee and login time are required.', 'warning');
    try {
      const r = await api.post('/training/work-attendance/sessions', {
        employeeId: dlgAddSession.employeeId,
        loginTime:  dlgAddSession.loginTime,
        logoutTime: dlgAddSession.logoutTime || null,
        notes:      dlgAddSession.notes || '',
      });
      if (r.data?.success !== false) {
        showAlert('Session created successfully.', 'success');
        setDlgAddSession(null);
        fetchRecords();
        fetchCalendar();
      } else showAlert(r.data?.message || 'Failed.', 'danger');
    } catch(e) { showAlert(e.response?.data?.message || 'Failed.', 'danger'); }
  };

  const submitAutoFill = async () => {
    setAutoFilling(true);
    try {
      const r = await api.post('/training/work-attendance/auto-fill-month', autoFillForm);
      if (r.data?.success !== false) {
        showAlert(r.data?.message || 'Auto-fill complete.', 'success');
        if (activeTab === 'records') fetchRecords();
      } else showAlert(r.data?.message || 'Failed.', 'danger');
    } catch(e) { showAlert(e.response?.data?.message || 'Failed.', 'danger'); }
    setAutoFilling(false);
  };

  // ── Save Policy ──────────────────────────────────────────────────────────
  const savePolicy = async () => {
    setPolSaving(true);
    try {
      const r = await api.put('/training/work-attendance/policy', polForm);
      if(r.data?.success!==false){ setPolicy(r.data?.data||polForm); showAlert('Policy saved.','success'); }
      else showAlert(r.data?.message||'Failed.','danger');
    } catch(e){ showAlert(e.response?.data?.message||'Failed.','danger'); }
    setPolSaving(false);
  };

  const isClockedIn = clockStatus?.isClockedIn;
  const todayRec    = clockStatus?.record;
  // Prefer the currently-active session (from the /clock-status endpoint).
  // If there isn't one, fall back to the most recent ended session in the log.
  const activeSess  = clockStatus?.activeSession || null;
  const lastSess    = activeSess
    || (todayRec?.sessions?.length ? todayRec.sessions[todayRec.sessions.length - 1] : null);
  const onLunch     = !!(activeSess?.lunchStartTime && !activeSess?.lunchEndTime);

  // Track browser activity and update lastInteractionTime on backend
  useActivityTracker({
    attendanceId: activeSess?._id,
    employeeId: user?._id,
    enabled: !!activeSess?._id && isClockedIn,
    updateInterval: 30000,
  });
  // Lunch is one-per-day. "Already taken" means: either it's in progress,
  // or the daily log has any lunchMinutes recorded, or the active session
  // started (and possibly finished) a lunch earlier in this shift.
  const lunchTakenToday = onLunch
    || (todayRec?.lunchMinutes > 0)
    || !!(activeSess?.lunchEndTime);

  // Class-tracking — server tells us whether this user has the right role
  // to see the button at all (covers multi-role admins who also teach),
  // and whether a class window is currently open. Local fallback uses the
  // user's own roleKey/additionalRoleKeys for the very first render before
  // /clock-status returns.
  const TEACHING_KEYS = ['faculty', 'teacher', 'trainer'];
  const localTeaching = TEACHING_KEYS.includes(user?.roleKey)
    || (Array.isArray(user?.additionalRoleKeys) && user.additionalRoleKeys.some(k => TEACHING_KEYS.includes(k)));
  const isTeachingRoleUI = clockStatus?.isTeachingRole ?? localTeaching;
  const activeClass      = clockStatus?.activeClass || null;
  const scheduledClass   = clockStatus?.scheduledClass || null;
  const inClass          = !!activeClass;

  const TABS = [
    { key:'my',         label:'My Attendance',      icon:'bi-calendar3' },
    { key:'activities', label:'Official Activities', icon:'bi-briefcase' },
    ...(isEmployee && !canAdmin ? [
      { key:'my_corrections', label:'My Corrections', icon:'bi-pencil-square' },
    ] : []),
    ...(canAdmin ? [
      { key:'team',            label:'Team Today',         icon:'bi-people' },
      { key:'bulk_entry',      label:'Bulk Entry',         icon:'bi-people-fill' },
      { key:'records',         label:'All Records',        icon:'bi-table' },
      { key:'employee_shifts', label:'Employee Shifts',    icon:'bi-clock' },
      { key:'corrections',     label:'Correction Requests', icon:'bi-pencil-square' },
      { key:'devices',         label:'Authorized Devices', icon:'bi-shield-lock', badge: pendingDeviceCount },
      { key:'monthly_reports', label:'Monthly Reports',    icon:'bi-file-earmark-spreadsheet' },
      { key:'policy',          label:'Policy Settings',    icon:'bi-sliders' },
    ] : []),
    { key:'manual',  label:'Manual / Instructions', icon:'bi-journal-text' },
  ];

  return (
    <div className="container-fluid py-3">
      <AlertToast message={alert.msg} type={alert.type} show={alert.show} onClose={()=>setAlert(p=>({...p,show:false}))}/>
      <PageHeader title="Work Attendance" icon="bi-clock-history" subtitle="Track daily work sessions and manage team attendance"/>

      {/* ── Clock Panel ─────────────────────────────────────────────────── */}
      {isEmployee && (
        <div className={`card mb-3 border-${inClass?'primary':onLunch?'warning':isClockedIn?'success':'secondary'}`}>
          <div className="card-body">
            {/* Device lock banner — appears when the server rejected clock-in due to device authorization */}
            {deviceError && (
              <div className={`alert alert-${deviceError==='rejected'?'danger':'warning'} d-flex align-items-start gap-2 py-2 mb-3`} style={{fontSize:13}}>
                <i className={`bi bi-${deviceError==='rejected'?'x-circle-fill':'shield-exclamation'} fs-5 flex-shrink-0 mt-1`}/>
                <div className="flex-grow-1">
                  {deviceError==='unregistered' && <><strong>Device not recognized.</strong> A registration request has been submitted. Your admin will approve it shortly — you'll be notified.</>}
                  {deviceError==='pending'      && <><strong>Authorization pending.</strong> This device is awaiting admin approval. Please wait before trying to clock in again.</>}
                  {deviceError==='rejected'     && <><strong>Device rejected.</strong> This device was not authorized by your admin. Please contact admin or use a registered system.</>}
                  {deviceError==='mismatch'     && <><strong>Hardware mismatch.</strong> This does not appear to be the registered machine. Clock-in is only allowed from your original authorized system.</>}
                  {deviceError==='correction_pending' && <><strong>Clock-out correction pending.</strong> You have a pending clock-out correction request that needs admin approval. Please wait for approval before clocking in again.</>}
                  {canAdmin && (deviceError==='unregistered'||deviceError==='pending') && (
                    <div className="mt-1">
                      <button className="btn btn-sm btn-warning py-0 px-2" onClick={()=>{ setActiveTab('devices'); setDevicesFilter('pending'); }}>
                        <i className="bi bi-shield-check me-1"/>Open Authorized Devices to approve →
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="row g-3 align-items-start">
              {/* Clock In/Out — isolated, far left */}
              <div className="col-md-2">
                {!isClockedIn ? (
                  <button className="btn btn-success w-100 py-2" onClick={()=>handleClock('clock-in')}
                          disabled={clockLoading || clockStatus?.alreadyClockedInToday || !!todayRec?.isAdminOverride}
                          title={
                            clockStatus?.alreadyClockedInToday ? 'You have already clocked in once today.'
                            : todayRec?.isAdminOverride        ? 'Your attendance for today was recorded by admin — no action needed.'
                            : ''
                          }>
                    {clockLoading
                      ? <span className="spinner-border spinner-border-sm"/>
                      : clockStatus?.alreadyClockedInToday
                        ? <><i className="bi bi-check2-circle me-1"/>Done for Today</>
                        : todayRec?.isAdminOverride
                          ? <><i className="bi bi-shield-check me-1"/>Admin Recorded</>
                          : <><i className="bi bi-play-fill me-1"/>Clock In</>}
                  </button>
                ) : (
                  <button className="btn btn-danger w-100 py-2" onClick={()=>handleClock('clock-out')}
                    disabled={clockLoading || onLunch || inClass}
                    title={inClass ? 'End class before clocking out' : (onLunch ? 'End lunch before clocking out' : '')}>
                    {clockLoading?<span className="spinner-border spinner-border-sm"/>:<><i className="bi bi-stop-fill me-1"/>Clock Out</>}
                  </button>
                )}
              </div>

              {/* Status indicator */}
              <div className="col-md-2 text-center">
                <div
                  className={`rounded-circle d-inline-flex align-items-center justify-content-center mb-1 ${inClass?'bg-primary':onLunch?'bg-warning':isClockedIn?'bg-success':'bg-secondary'}`}
                  style={{width:60,height:60}}
                >
                  <i className={`bi bi-${inClass?'mortarboard-fill':onLunch?'cup-hot-fill':isClockedIn?'record-circle-fill':'circle'} text-white fs-3`}/>
                </div>
                <div className="fw-bold small">
                  {inClass ? 'IN CLASS' : onLunch ? 'ON LUNCH' : isClockedIn ? 'CLOCKED IN' : 'CLOCKED OUT'}
                </div>
                {isClockedIn && lastSess?.loginTime && (
                  <div className="small mt-1">
                    Elapsed: <LiveClock loginTime={lastSess.loginTime}/>
                  </div>
                )}
                {inClass && activeClass?.startTime && (
                  <div className="small mt-1 text-primary">
                    Class: <LiveClock loginTime={activeClass.startTime}/>
                  </div>
                )}
                {onLunch && activeSess?.lunchStartTime && (
                  <div className="small mt-1 text-warning">
                    Lunch: <LiveClock loginTime={activeSess.lunchStartTime}/>
                  </div>
                )}
                {!isClockedIn && todayRec?.totalWorkedMinutes>0 && (
                  <div className="small text-muted">Today: {fmtMins(todayRec.totalWorkedMinutes)}</div>
                )}
                {!isClockedIn && todayRec?.isAdminOverride && (
                  <div className="badge bg-info-subtle text-info-emphasis border border-info-subtle small mt-1" style={{fontSize:'0.7rem'}}>
                    <i className="bi bi-shield-check me-1"/>Admin recorded
                  </div>
                )}
              </div>

              {/* Lunch + Class — grouped together, separate from Clock buttons */}
              <div className="col-md-3 d-flex flex-column gap-2">
                {isTeachingRoleUI && (
                  inClass ? (
                    <button className="btn btn-warning w-100 py-2" onClick={()=>handleClock('class-end')} disabled={clockLoading}>
                      {clockLoading ? <span className="spinner-border spinner-border-sm"/> : <><i className="bi bi-mortarboard-fill me-1"/>End Class</>}
                    </button>
                  ) : (
                    <>
                      {(!scheduledClass) && (
                        <input
                          type="text"
                          className="form-control form-control-sm mb-1"
                          placeholder="Course name (e.g. JavaScript Basics)"
                          value={classCourseName}
                          onChange={e => setClassCourseName(e.target.value)}
                        />
                      )}
                      <button
                        className="btn btn-primary w-100 py-2"
                        onClick={()=>handleClock('class-start')}
                        disabled={clockLoading || !scheduledClass}
                        title={scheduledClass ? `Scheduled: ${scheduledClass.batchLabel || scheduledClass.courseLabel || 'class'}` : 'No class scheduled for this time'}>
                        {clockLoading ? <span className="spinner-border spinner-border-sm"/> : <><i className="bi bi-mortarboard me-1"/>Going to Class</>}
                      </button>
                    </>
                  )
                )}
                {onLunch ? (
                  <button className="btn btn-outline-warning w-100 py-2" onClick={()=>handleClock('lunch-end')} disabled={clockLoading}>
                    <i className="bi bi-arrow-return-left me-1"/>End Lunch
                  </button>
                ) : lunchTakenToday ? (
                  <span className="btn btn-outline-secondary disabled w-100 py-2" style={{ cursor: 'not-allowed' }} title={`Lunch taken: ${fmtMins(todayRec?.lunchMinutes || 0)}`}>
                    <i className="bi bi-check2-circle me-1"/>Lunch Done
                  </span>
                ) : (
                  <button className="btn btn-outline-warning w-100 py-2" onClick={()=>handleClock('lunch-start')}
                    disabled={clockLoading || inClass}
                    title={inClass ? 'End class before lunch' : ''}>
                    <i className="bi bi-cup-hot me-1"/>Go for Lunch
                  </button>
                )}
              </div>

              {/* Note */}
              <div className="col-md-5">
                <label className="form-label small mb-1">Note (optional)</label>
                <input className="form-control form-control-sm mb-2" value={clockNote} onChange={e=>setClockNote(e.target.value)} placeholder="E.g., WFH, client site…"/>
                {todayRec?.lunchMinutes > 0 && (
                  <div className="small text-muted">
                    <i className="bi bi-cup-hot me-1"/>Lunch today: {fmtMins(todayRec.lunchMinutes)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tabs ────────────────────────────────────────────────────────── */}
      <ul className="nav nav-tabs mb-3">
        {TABS.map(t=>(
          <li key={t.key} className="nav-item">
            <button className={`nav-link ${activeTab===t.key?'active':''}`} onClick={()=>setActiveTab(t.key)}>
              <i className={`bi ${t.icon} me-1`}/>{t.label}
              {t.badge > 0 && <span className="badge bg-danger ms-1" style={{fontSize:'0.65rem'}}>{t.badge}</span>}
            </button>
          </li>
        ))}
      </ul>

      {/* ── Tab 1: My Attendance ─────────────────────────────────────────── */}
      {activeTab==='my' && (
        <div>
          {/* Daily happy banner — one rotating message per day, personalized. */}
          {!viewedUserId && isEmployee && !clockStatus?.staleSession && (
            <div className="alert alert-light border-start border-4 border-primary py-2 px-3 mb-3 d-flex align-items-center gap-2"
                 style={{background:'linear-gradient(90deg, #eef2ff 0%, #fff 100%)'}}>
              <i className="bi bi-sun fs-4 text-warning"/>
              <div className="fw-semibold flex-grow-1" style={{fontSize:14}}>
                {pickDaily(HAPPY_MESSAGES, user?.firstName)}
              </div>
            </div>
          )}

          {/* Stale session warning — shown to the employee when they have an open
               session from a previous day that was never closed. */}
          {!viewedUserId && clockStatus?.staleSession && (
            <div className="alert alert-danger border-start border-4 border-danger mb-3" style={{fontSize:14}}>
              <div className="d-flex align-items-start gap-2">
                <i className="bi bi-exclamation-octagon-fill fs-5 mt-1 flex-shrink-0"/>
                <div className="flex-grow-1">
                  <div className="fw-bold mb-1">You forgot to clock out!</div>
                  <div className="mb-2">
                    Your session from <strong>{fmtDate(clockStatus.staleSessionDate)}</strong> (clocked in at <strong>{fmtTime(clockStatus.staleLoginTime)}</strong>) was never closed.
                    You cannot clock in today until this is fixed.
                  </div>
                  <button className="btn btn-danger btn-sm"
                    onClick={() => setDlgSelfFix({
                      logoutTime: '',
                      reason: 'Forgot to clock out',
                    })}>
                    <i className="bi bi-clock-history me-1"/>Fix My Logout
                  </button>
                  {canAdmin ? (
                    <span className="text-muted ms-3 small">Submit a correction request which you can approve from the Correction Requests tab.</span>
                  ) : (
                    <span className="text-muted ms-3 small">or ask your admin to fix it for you.</span>
                  )}
                  <button className="btn btn-outline-warning btn-sm ms-2"
                    onClick={async () => {
                      if (!window.confirm('Create a test stale session? This will simulate forgetting to clock out.')) return;
                      try {
                        await api.post('/training/work-attendance/create-test-stale-session');
                        showAlert('Test stale session created! Refresh the page.', 'info');
                      } catch(e) { showAlert('Failed to create test session.', 'danger'); }
                    }}>
                    <i className="bi bi-flask me-1"/>Test Stale Session
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Admin user selector — dropdown to view individual employee attendance */}
          {canAdmin && (
            <div className="mb-3 d-flex align-items-center gap-2">
              <label className="text-muted fw-semibold small mb-0">Select Employee:</label>
              <select
                className="form-select form-select-sm"
                style={{ width: 220 }}
                value={viewedUserId}
                onChange={e => { setViewedUserId(e.target.value); setExpandedRecord(null); }}
              >
                <option value="">— Me (My Attendance) —</option>
                {staffList.map(u => (
                  <option key={u._id} value={u._id}>
                    {u.firstName} {u.lastName} ({u.roleKey || '—'})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Month/Year selector */}
          <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
            <select className="form-select form-select-sm" style={{width:140}} value={calMonth} onChange={e=>setCalMonth(Number(e.target.value))}>
              {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{width:90}} value={calYear} onChange={e=>setCalYear(Number(e.target.value))}>
              {[2024,2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
            </select>
            <button className="btn btn-outline-secondary btn-sm" onClick={fetchCalendar}><i className="bi bi-arrow-clockwise me-1"/>Refresh</button>
          </div>

          {/* Stats row */}
          {stats && (
            <div className="row g-2 mb-3">
              <div className="col-6 col-md-2"><StatCard title="Present"     value={stats.present||0}                       icon="bi-person-check"   color="#198754"/></div>
              <div className="col-6 col-md-2"><StatCard title="Absent"      value={stats.absent||0}                        icon="bi-person-x"       color="#dc3545"/></div>
              <div className="col-6 col-md-2"><StatCard title="Leave"       value={stats.onLeave||0}                       icon="bi-calendar-x"     color="#0d6efd"/></div>
              <div className="col-6 col-md-2"><StatCard title="Half Day"    value={stats.halfDay||0}                       icon="bi-calendar2-half" color="#fd7e14"/></div>
              <div className="col-6 col-md-2"><StatCard title="Total Hours" value={fmtMins(stats.totalMins||0)}            icon="bi-clock"          color="#6f42c1"/></div>
              <div className="col-6 col-md-2"><StatCard title="Working Days"value={stats.totalDays||'—'}                   icon="bi-calendar-week"  color="#0dcaf0"/></div>
            </div>
          )}

          {/* Calendar */}
          <div className="card mb-3">
            <div className="card-header fw-semibold"><i className="bi bi-calendar3 me-1"/>Calendar — {MONTHS[calMonth-1]} {calYear}</div>
            <div className="card-body">
              {calDays.length ? renderCalendar(calDays) : <div className="text-center py-3 text-muted">No data for this month.</div>}
              <div className="d-flex flex-wrap gap-2 mt-3">
                {Object.entries(STATUS_BG).filter(([k])=>k!=='future').map(([k,v])=>(
                  <span key={k} className="badge" style={{background:v,color:['weekend','future'].includes(k)?'#333':'#fff',fontSize:11}}>
                    {k.replace('_',' ')}
                  </span>
                ))}
                <span className="badge bg-light text-muted border" style={{fontSize:11}}>future</span>
              </div>
            </div>
          </div>

          {/* Activities / meetings panel — shown alongside the records so an
              admin (or the user themselves) can see meetings + off-desk
              activities at a glance without bouncing tabs. */}
          {viewedUserActivities.length > 0 && (
            <div className="card mb-3">
              <div className="card-header fw-semibold d-flex justify-content-between align-items-center">
                <span><i className="bi bi-bell me-1 text-warning"/>Activities &amp; Meetings</span>
                <span className="badge bg-light text-muted border">{viewedUserActivities.length}</span>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light"><tr>
                      <th>Submitted</th><th>Reason</th><th>Description</th><th>Expected</th><th>Actual</th><th>Status</th>
                    </tr></thead>
                    <tbody>
                      {viewedUserActivities.map(a => (
                        <tr key={a._id}>
                          <td className="small">{fmtDate(a.createdAt)}</td>
                          <td><span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">{REASON_LABELS[a.reason] || a.reason}</span></td>
                          <td className="small">{a.description || '—'}</td>
                          <td className="small">{fmtMins(a.expectedDurationMinutes||0)}</td>
                          <td className="small">{a.actualDurationMinutes ? fmtMins(a.actualDurationMinutes) : '—'}</td>
                          <td>
                            <span className={`badge bg-${a.status==='approved'?'success':a.status==='rejected'?'danger':'warning'} ${a.status==='pending'?'text-dark':''}`}>
                              {a.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Records — every day of the selected month (newest first).
               Full grid is computed on the frontend so weekends and absent days
               always appear even if the calendar API hasn't loaded.
               Days with a DB record are expandable; gap rows are non-clickable. */}
          {(() => {
            // Weekend days from work policy, default Sat+Sun.
            const weekendDays = policy?.weekendDays || [0, 6];

            // Build every day of the selected month.
            const todayStr   = todayISO();
            const daysInMonth = new Date(calYear, calMonth, 0).getDate();
            const rowMap = {};
            for (let d = 1; d <= daysInMonth; d++) {
              const dateStr  = `${calYear}-${String(calMonth).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
              const dow      = new Date(dateStr).getDay();
              const isWeekend = weekendDays.includes(dow);
              const isFuture  = dateStr > todayStr;
              const status    = isWeekend ? 'weekend' : isFuture ? 'future' : 'absent';
              rowMap[dateStr] = { dateStr, status, totalMins: 0, isWeekend, isFuture, isStub: true, rec: null };
            }

            // Overlay real attendance records — override the stub for that day.
            myRecords.forEach(r => {
              const d = new Date(r.date).toISOString().split('T')[0];
              if (rowMap[d]) {
                rowMap[d] = { dateStr: d, status: r.status || 'absent', totalMins: r.totalWorkedMinutes || 0, isWeekend: false, isFuture: false, isStub: false, rec: r };
              }
            });

            calDays.forEach(day => {
              const row = rowMap[day.date];
              if (!row) return;
              row.status = day.status;
              if (day.leaveLabel) row.rec = { ...(row.rec || {}), leaveLabel: day.leaveLabel };
            });
            const displayRows = Object.values(rowMap).sort((a, b) => a.dateStr.localeCompare(b.dateStr));
            const totalShown  = daysInMonth;

            return (
              <div className="card">
                <div className="card-header fw-semibold d-flex justify-content-between align-items-center">
                  <span><i className="bi bi-table me-1"/>Records — {MONTHS[calMonth-1]} {calYear}</span>
                  <div className="d-flex align-items-center gap-2">
                    {canAdmin && (
                      <button className="btn btn-sm btn-outline-primary" onClick={() => setDlgAddSession({ employeeId: viewedUserId || '', date: `${calYear}-${String(calMonth).padStart(2,'0')}-01`, loginTime: '', logoutTime: '', notes: '' })}>
                        <i className="bi bi-plus-circle me-1"/>Add Session
                      </button>
                    )}
                    <small className="text-muted">{totalShown} days</small>
                  </div>
                </div>
                <div className="card-body p-0">
                  {displayRows.length === 0 ? (
                    <div className="text-center py-4 text-muted">No records for this month.</div>
                  ) : (
                    <div>
                      {displayRows.map(({ dateStr, status, totalMins, isStub, isWeekend, rec }) => {
                        const rowKey   = rec?._id || dateStr;
                        const isOpen   = expandedRecord === rowKey;
                        const isFaded  = isStub && (isWeekend || status === 'future');
                        return (
                          <div key={rowKey}>
                            {/* ── Compact row ── */}
                            <div
                              className={`d-flex align-items-center gap-3 px-3 py-2 border-bottom${isFaded ? ' opacity-50' : ''}`}
                              style={{
                                cursor: isStub ? 'default' : 'pointer',
                                background: isOpen ? '#f8f9fa' : (isStub && status === 'absent' ? '#fff8f8' : 'transparent'),
                                userSelect: 'none',
                              }}
                              onClick={() => !isStub && setExpandedRecord(isOpen ? null : rowKey)}
                            >
                              <div style={{ minWidth: 110 }}>
                                <span className="fw-semibold" style={{ fontSize: 13 }}>{fmtDate(dateStr)}</span>
                                <span className="text-muted ms-1" style={{ fontSize: 11 }}>
                                  {new Date(dateStr).toLocaleDateString('en-IN', { weekday: 'short' })}
                                </span>
                              </div>
                              <span
                                className={`badge bg-${STATUS_BADGE[status] || 'secondary'} ${status === 'half_day' ? 'text-dark' : ''}`}
                                style={{ minWidth: 75, fontSize: 11, ...(status === 'holiday' ? { backgroundColor: STATUS_BG.holiday, color: '#fff' } : {}) }}
                              >
                                {rec?.leaveLabel || STATUS_LABEL[status] || (status || '').replace('_', ' ')}
                              </span>
                              {rec?.overrideReason && status !== 'holiday' && !rec?.leaveLabel && (
                                <span className="badge bg-info text-white" style={{ fontSize: 10 }} title={rec.overrideReason}>
                                  <i className="bi bi-pencil-square me-1"/>Admin Adjusted
                                </span>
                              )}
                              <span className="small text-muted" style={{ minWidth: 58 }}>
                                <i className="bi bi-box-arrow-in-right text-success me-1" style={{ fontSize: 10 }}/>
                                {rec?.firstLogin?.time ? fmtTime(rec.firstLogin.time) : '—'}
                              </span>
                              <span className="small text-muted" style={{ minWidth: 58 }}>
                                <i className="bi bi-box-arrow-right text-danger me-1" style={{ fontSize: 10 }}/>
                                {rec?.lastLogout?.time ? fmtTime(rec.lastLogout.time) : '—'}
                              </span>
                              <span className={`fw-semibold small ms-auto ${isStub ? 'text-muted' : 'text-primary'}`}>
                                {fmtMins(rec?.totalWorkedMinutes || totalMins || 0)}
                              </span>
                              {!isStub
                                ? <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} text-muted`} style={{ fontSize: 12 }}/>
                                : <span style={{ minWidth: 12 }}/>
                              }
                            </div>

                            {/* ── Expanded detail (real records only) ── */}
                            {isOpen && rec && (
                              <div className="px-4 py-3 border-bottom bg-light" style={{ fontSize: 13 }}>
                                <div className="row g-3">
                                  <div className="col-12 col-md-5">
                                    <div className="fw-semibold mb-2"><i className="bi bi-clock-history me-1 text-primary"/>Sessions</div>
                                    {Array.isArray(rec.sessions) && rec.sessions.length > 0 ? (
                                      <div className="d-flex flex-column gap-1">
                                        {rec.sessions.map((s, i) => (
                                          <div key={s._id || i} className="d-flex align-items-center gap-2 flex-wrap">
                                            <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle" style={{ fontSize: 11 }}>#{i + 1}</span>
                                            <span className="text-muted small">
                                              {s.loginTime ? fmtTime(s.loginTime) : '—'}
                                              {' → '}
                                              {s.logoutTime ? fmtTime(s.logoutTime) : s.status === 'active' ? <span className="text-success">(active)</span> : '—'}
                                            </span>
                                            {s.duration ? <span className="small text-primary ms-1">{fmtMins(s.duration)}</span> : null}
                                            {(s.status === 'forced_logout' || s.status === 'timeout') && (
                                              <>
                                                <span className="badge bg-warning text-dark ms-1" style={{ fontSize: 10 }}>
                                                  <i className="bi bi-clock-history me-1"/>Auto
                                                </span>
                                                {isEmployee && (
                                                  <button className="btn btn-sm btn-outline-warning py-0 px-1 ms-auto"
                                                    style={{ fontSize: 10 }}
                                                    onClick={() => setDlgCorrection({ sessionId: s._id, logoutTime: s.logoutTime, reason: '' })}>
                                                    <i className="bi bi-pencil-square me-1"/>Request Correction
                                                  </button>
                                                )}
                                              </>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-muted small">No session data</span>
                                    )}
                                  </div>
                                  <div className="col-12 col-md-4">
                                    <div className="fw-semibold mb-2"><i className="bi bi-bar-chart me-1 text-warning"/>Time Breakdown</div>
                                    <div className="d-flex flex-column gap-1">
                                      <div className="d-flex justify-content-between small"><span className="text-muted">Worked</span><span className="fw-semibold">{fmtMins(rec.totalWorkedMinutes || 0)}</span></div>
                                      <div className="d-flex justify-content-between small"><span className="text-muted">Lunch break</span><span className={rec.lunchMinutes ? 'text-warning fw-semibold' : 'text-muted'}>{rec.lunchMinutes ? fmtMins(rec.lunchMinutes) : '—'}</span></div>
                                      <div className="d-flex justify-content-between small"><span className="text-muted">Off-desk</span><span className={rec.officialActivityMinutes ? 'text-info fw-semibold' : 'text-muted'}>{rec.officialActivityMinutes ? fmtMins(rec.officialActivityMinutes) : '—'}</span></div>
                                    </div>
                                  </div>
                                  {rec.note && (
                                    <div className="col-12 col-md-3">
                                      <div className="fw-semibold mb-2"><i className="bi bi-chat-left-text me-1 text-secondary"/>Note</div>
                                      <div className="small text-muted">{rec.note}</div>
                                    </div>
                                  )}
                                  {rec.overrideReason && (
                                    <div className="col-12">
                                      <div className="alert alert-warning py-2 px-3 mb-0" style={{fontSize:12}}>
                                        <i className="bi bi-exclamation-triangle me-1"/>{rec.overrideReason}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Tab 2: Official Activities ───────────────────────────────────── */}
      {activeTab==='activities' && (
        <div>
          {/* Submit form */}
          <div className="card mb-3">
            <div className="card-header fw-semibold"><i className="bi bi-plus-circle me-1"/>Submit Off-Desk Activity</div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Reason</label>
                  <select className="form-select" value={actForm.reason} onChange={e=>setActForm(p=>({...p,reason:e.target.value}))}>
                    {ACTIVITY_REASONS.map(r=><option key={r} value={r}>{REASON_LABELS[r]}</option>)}
                  </select>
                </div>
                <div className="col-md-5">
                  <label className="form-label">Description</label>
                  <input className="form-control" value={actForm.description} onChange={e=>setActForm(p=>({...p,description:e.target.value}))} placeholder="Brief description…"/>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Expected Mins</label>
                  <input className="form-control" type="number" min={1} value={actForm.expectedMins} onChange={e=>setActForm(p=>({...p,expectedMins:e.target.value}))} placeholder="60"/>
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <button className="btn btn-primary w-100" onClick={submitActivity} disabled={actSubmitting}>
                    {actSubmitting?<span className="spinner-border spinner-border-sm"/>:<><i className="bi bi-send me-1"/>Submit</>}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Admin user filter — pick a user to see only their activities. */}
          {canAdmin && (
            <div className="card mb-3 border-info">
              <div className="card-body py-2">
                <div className="row g-2 align-items-center">
                  <div className="col-auto">
                    <span className="fw-semibold small"><i className="bi bi-funnel me-1 text-info"/>Filter by user</span>
                  </div>
                  <div className="col-md-3">
                    <select className="form-select form-select-sm" value={activityFilterUser}
                      onChange={e=>{ setActivityFilterUser(e.target.value); setActPage(1); }}>
                      <option value="">— All users —</option>
                      {staffList.map(u => (
                        <option key={u._id} value={u._id}>
                          {u.firstName} {u.lastName} ({u.roleKey || '—'})
                        </option>
                      ))}
                    </select>
                  </div>
                  {activityFilterUser && (
                    <div className="col-auto">
                      <button className="btn btn-outline-secondary btn-sm" onClick={()=>{ setActivityFilterUser(''); setActPage(1); }}>
                        <i className="bi bi-x-lg me-1"/>Clear
                      </button>
                    </div>
                  )}
                  <div className="col-auto small text-muted">
                    Showing {activities.length} activit{activities.length === 1 ? 'y' : 'ies'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Activities — clean table view (was per-card grid). */}
          <div className="card">
            <div className="card-header fw-semibold d-flex justify-content-between align-items-center">
              <span><i className="bi bi-briefcase me-1"/>Off-Desk Activities</span>
            </div>
            <div className="card-body p-0">
              {activities.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-briefcase fs-1 d-block mb-2"/>No activities found.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light"><tr>
                      {canAdmin && <th>Employee</th>}
                      <th>Reason</th>
                      <th>Description</th>
                      <th>Expected</th>
                      <th>Actual</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      {canAdmin && <th>Actions</th>}
                    </tr></thead>
                    <tbody>
                      {activities.map(act => (
                        <tr key={act._id}>
                          {canAdmin && (
                            <td>
                              <div className="fw-semibold small">
                                {act.employee?.firstName || '—'} {act.employee?.lastName || ''}
                              </div>
                              <div className="small text-muted">{act.employee?.roleKey || ''}</div>
                            </td>
                          )}
                          <td>
                            <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
                              {REASON_LABELS[act.reason] || act.reason}
                            </span>
                          </td>
                          <td className="small" style={{maxWidth: 260}}>{act.description || '—'}</td>
                          <td className="small">{fmtMins(act.expectedDurationMinutes||0)}</td>
                          <td className="small">{act.actualDurationMinutes ? fmtMins(act.actualDurationMinutes) : '—'}</td>
                          <td>
                            <span className={`badge bg-${act.status==='approved'?'success':act.status==='rejected'?'danger':'warning'} ${act.status==='pending'?'text-dark':''}`}>
                              {act.status}
                            </span>
                            {act.approvalNote && (
                              <div className="small text-muted mt-1" title={act.approvalNote}>
                                <i className="bi bi-chat-text me-1"/>{act.approvalNote.length > 40 ? `${act.approvalNote.slice(0,40)}…` : act.approvalNote}
                              </div>
                            )}
                          </td>
                          <td className="small text-muted">{fmtDate(act.createdAt)}</td>
                          {canAdmin && (
                            <td>
                              {act.status === 'pending' ? (
                                <button className="btn btn-outline-primary btn-sm" onClick={()=>setDlgApprove(act)}>
                                  <i className="bi bi-check2-circle me-1"/>Review
                                </button>
                              ) : (
                                <span className="text-muted small">—</span>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {actTotal>1 && (
            <nav className="mt-3 d-flex justify-content-center">
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${actPage<=1?'disabled':''}`}><button className="page-link" onClick={()=>setActPage(p=>Math.max(1,p-1))}>‹</button></li>
                {Array.from({length:actTotal},(_,i)=>i+1).map(p=>(
                  <li key={p} className={`page-item ${p===actPage?'active':''}`}><button className="page-link" onClick={()=>setActPage(p)}>{p}</button></li>
                ))}
                <li className={`page-item ${actPage>=actTotal?'disabled':''}`}><button className="page-link" onClick={()=>setActPage(p=>Math.min(actTotal,p+1))}>›</button></li>
              </ul>
            </nav>
          )}
        </div>
      )}

      {/* ── Tab: My Corrections (employee) ────────────────────────────────── */}
      {activeTab==='my_corrections' && !canAdmin && (
        <div className="row g-3">
          <div className="col-12">
            <div className="card">
              <div className="card-header fw-semibold d-flex justify-content-between align-items-center">
                <span><i className="bi bi-pencil-square me-1"/>My Correction Requests</span>
                <span className="badge bg-warning text-dark">{myCorrections.filter(r => r.status === 'pending').length} pending</span>
              </div>
              <div className="card-body p-0">
                {myCorrections.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-check-circle fs-1 d-block mb-2"/>
                    No correction requests.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Status</th>
                          <th>Original Time</th>
                          <th>Suggested Time</th>
                          <th>Reason</th>
                          <th>Submitted</th>
                          <th className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myCorrections.map(req => (
                          <tr key={req._id}>
                            <td>
                              {req.status === 'pending' && <span className="badge bg-warning text-dark">Pending</span>}
                              {req.status === 'approved' && !req.createdByAdmin && <span className="badge bg-success">Admin Approved</span>}
                              {req.status === 'approved' && req.createdByAdmin && <span className="badge bg-success">Approved</span>}
                              {req.status === 'rejected' && <span className="badge bg-danger">Rejected</span>}
                            </td>
                            <td className="small">{req.originalLogoutTime ? fmtTime(req.originalLogoutTime) : '—'} <span className="text-muted">{fmtDate(req.originalLogoutTime)}</span></td>
                            <td className="small text-success fw-semibold">{req.requestedLogoutTime ? fmtTime(req.requestedLogoutTime) : '—'} <span className="text-muted">{fmtDate(req.requestedLogoutTime)}</span></td>
                            <td className="small" style={{maxWidth:200}}>
                              {req.createdByAdmin ? <span className="text-primary">Admin: </span> : ''}{req.reason}
                              {req.adminNotes && <div className="text-muted small mt-1"><em>Note: {req.adminNotes}</em></div>}
                            </td>
                            <td className="small text-muted">{fmtDate(req.createdAt)} {fmtTime(req.createdAt)}</td>
                            <td className="text-end">
                              {req.status === 'pending' && (
                                <div className="d-flex gap-1 justify-content-end">
                                  <button className="btn btn-sm btn-success" title="Approve"
                                    onClick={async () => {
                                      try {
                                        const r = await api.patch(`/training/work-attendance/my-clock-out-correction-requests/${req._id}`, { status: 'approved' });
                                        if (r.data?.success !== false) { showAlert('Correction approved.', 'success'); fetchMyCorrections(); }
                                        else showAlert(r.data?.message || 'Failed.', 'danger');
                                      } catch(e) { showAlert(e.response?.data?.message || 'Failed.', 'danger'); }
                                    }}>
                                    <i className="bi bi-check-lg"/>Approve
                                  </button>
                                  <button className="btn btn-sm btn-danger" title="Reject"
                                    onClick={async () => {
                                      const notes = prompt('Reason for rejection (optional):');
                                      try {
                                        const r = await api.patch(`/training/work-attendance/my-clock-out-correction-requests/${req._id}`, { status: 'rejected', notes });
                                        if (r.data?.success !== false) { showAlert('Correction rejected.', 'info'); fetchMyCorrections(); }
                                        else showAlert(r.data?.message || 'Failed.', 'danger');
                                      } catch(e) { showAlert(e.response?.data?.message || 'Failed.', 'danger'); }
                                    }}>
                                    <i className="bi bi-x-lg"/>Reject
                                  </button>
                                </div>
                              )}
                              {req.status !== 'pending' && <span className="text-muted small">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              {myCorrectionTotal > 1 && (
                <div className="card-footer d-flex justify-content-center">
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${myCorrectionPage <= 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setMyCorrectionPage(p => Math.max(1, p - 1))}>‹</button>
                    </li>
                    {Array.from({ length: myCorrectionTotal }, (_, i) => i + 1).map(p => (
                      <li key={p} className={`page-item ${p === myCorrectionPage ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => setMyCorrectionPage(p)}>{p}</button>
                      </li>
                    ))}
                    <li className={`page-item ${myCorrectionPage >= myCorrectionTotal ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setMyCorrectionPage(p => Math.min(myCorrectionTotal, p + 1))}>›</button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab 3: Team Today (admin) ────────────────────────────────────── */}
      {activeTab==='team' && canAdmin && (
        <div>
          {/* Test button for admin */}
          <div className="mb-3 d-flex gap-2 align-items-center">
            <span className="text-muted small">Test:</span>
            <select className="form-select form-select-sm" style={{width:200}} id="testEmployeeSelect">
              <option value="">— Select Employee —</option>
              {staffList.filter(u => u.roleKey !== 'student').map(u => (
                <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
              ))}
            </select>
            <button className="btn btn-outline-warning btn-sm"
              onClick={() => {
                const sel = document.getElementById('testEmployeeSelect');
                const empId = sel?.value;
                if (!empId) return showAlert('Select an employee first.', 'warning');
                if (!window.confirm('Create test stale session for this employee?')) return;
                api.post('/training/work-attendance/create-test-stale-session-for', { employeeId: empId })
                  .then(() => { showAlert('Test stale session created. Refresh Team Today.', 'info'); fetchTeam(); })
                  .catch(e => showAlert('Failed.', 'danger'));
              }}>
              <i className="bi bi-flask me-1"/>Create Test Stale Session
            </button>
          </div>

          {/* Missing logout warning banner */}
          {teamToday.filter(item => {
            if (!item.isClockedIn || !item.activeSince) return false;
            return new Date(item.activeSince).toISOString().split('T')[0] < new Date().toISOString().split('T')[0];
          }).length > 0 && (
            <div className="alert alert-warning mb-3">
              <div className="fw-semibold mb-2"><i className="bi bi-exclamation-triangle-fill me-2"/>Employees who forgot to clock out:</div>
              <div className="d-flex flex-wrap gap-2">
                {teamToday.filter(item => {
                  if (!item.isClockedIn || !item.activeSince) return false;
                  return new Date(item.activeSince).toISOString().split('T')[0] < new Date().toISOString().split('T')[0];
                }).map((item, i) => (
                  <div key={i} className="d-flex align-items-center gap-2 bg-white border border-warning rounded px-2 py-1">
                    <span className="fw-semibold small">{item.user?.firstName} {item.user?.lastName}</span>
                    <span className="text-muted small">since {fmtTime(item.activeSince)}</span>
                    <button className="btn btn-warning btn-sm py-0 px-2" style={{fontSize:11}}
                      onClick={() => setDlgFixLogout({
                        sessionId: item.activeSessionId,
                        employeeName: `${item.user?.firstName} ${item.user?.lastName}`,
                        loginTime: item.activeSince,
                        logoutTime: '',
                        warningNote: 'Please remember to clock out every day before leaving.',
                      })}>
                      Fix Logout
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="fw-semibold fs-6"><i className="bi bi-people me-1"/>Team Status Today</span>
            <button className="btn btn-outline-secondary btn-sm" onClick={fetchTeam}><i className="bi bi-arrow-clockwise me-1"/>Refresh</button>
          </div>
          {teamLoading ? <div className="text-center py-5"><div className="spinner-border text-primary"/></div> : (
            <div className="row g-3">
              {teamToday.length===0 && <div className="col-12 text-center text-muted py-5">No team data available.</div>}
              {teamToday.map((item,i)=>{
                const worked = item.record?.totalWorkedMinutes || 0;
                const offDesk = item.record?.officialActivityMinutes || 0;
                const firstIn = item.record?.firstLogin?.time;
                return (
                  <div key={i} className="col-6 col-md-3 col-lg-2">
                    <div className={`card h-100 border-${item.isClockedIn?'success':'secondary'}`}>
                      <div className="card-body text-center p-2">
                        <div className={`rounded-circle d-inline-flex align-items-center justify-content-center mb-1 ${item.isClockedIn?'bg-success':'bg-secondary'}`} style={{width:40,height:40}}>
                          <i className="bi bi-person-fill text-white"/>
                        </div>
                        <div className="fw-semibold small">{item.user?.firstName} {item.user?.lastName}</div>
                        <div className="text-muted" style={{fontSize:11}}>{item.user?.roleKey}</div>
                        <div className="mt-1">
                          <span className={`badge ${item.isClockedIn?'bg-success':'bg-secondary'}`} style={{fontSize:10}}>
                            {item.isClockedIn?'IN':'OUT'}
                          </span>
                        </div>
                        {firstIn && (
                          <div className="small text-muted mt-1" title="First clock-in">
                            <i className="bi bi-box-arrow-in-right me-1"/>{fmtTime(firstIn)}
                          </div>
                        )}
                        {worked>0 && (
                          <div className="small mt-1" title="Total worked today">
                            <i className="bi bi-clock me-1"/>{fmtMins(worked)}
                          </div>
                        )}
                        {offDesk>0 && (
                          <div className="small text-primary mt-1" title="Official activity (off-desk) today">
                            <i className="bi bi-briefcase me-1"/>{fmtMins(offDesk)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Bulk Entry Tab (admin) ──────────────────────────────────────── */}
      {activeTab==='bulk_entry' && canAdmin && (
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card border-primary">
              <div className="card-header bg-primary text-white d-flex align-items-center gap-2">
                <i className="bi bi-people-fill fs-5"/>
                <div>
                  <div className="fw-semibold">Bulk Attendance Entry</div>
                  <div style={{fontSize:12,opacity:0.85}}>Apply one action to all employees (or a selected group) in a single step</div>
                </div>
              </div>
              <div className="card-body">
                {/* Result banner */}
                {bulkResult && (
                  <div className="alert alert-success d-flex align-items-center gap-2 py-2 mb-3">
                    <i className="bi bi-check-circle-fill fs-5"/>
                    <div>
                      <strong>Done!</strong> Updated <strong>{bulkResult.updated}</strong> employee{bulkResult.updated !== 1 ? 's' : ''}.
                      {bulkResult.skipped > 0 && <span className="text-muted ms-2">({bulkResult.skipped} skipped due to errors)</span>}
                    </div>
                  </div>
                )}

                <div className="row g-3">
                  {/* Date */}
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Date <span className="text-danger">*</span></label>
                    <input type="date" className="form-control"
                      value={bulkForm.date}
                      onChange={e => { setBulkForm(f=>({...f,date:e.target.value})); setBulkResult(null); }}/>
                  </div>

                  {/* Action */}
                  <div className="col-md-8">
                    <label className="form-label fw-semibold">Action <span className="text-danger">*</span></label>
                    <div className="d-flex flex-wrap gap-2">
                      {[
                        { key:'set',     label:'Set Hours',       icon:'bi-clock-fill',      color:'primary',  desc:'Replace totalworked hours for all employees' },
                        { key:'add',     label:'Add Hours',       icon:'bi-plus-circle-fill', color:'success', desc:'Add on top of whatever they already have' },
                        { key:'holiday', label:'Mark Holiday',    icon:'bi-calendar-x-fill', color:'info',     desc:'Set status = holiday, zero hours' },
                        { key:'absent',  label:'Mark Absent',     icon:'bi-person-x-fill',   color:'danger',   desc:'Set status = absent, zero hours' },
                      ].map(a => (
                        <div key={a.key}
                          className={`border rounded p-2 flex-fill text-center ${bulkForm.action===a.key?`border-${a.color} bg-${a.color} bg-opacity-10`:'border-secondary'}`}
                          role="button"
                          style={{cursor:'pointer',minWidth:110}}
                          onClick={() => { setBulkForm(f=>({...f,action:a.key, status: a.key==='holiday'?'holiday': a.key==='absent'?'absent':'present'})); setBulkResult(null); }}>
                          <i className={`bi ${a.icon} text-${a.color} d-block fs-4 mb-1`}/>
                          <div className="fw-semibold small">{a.label}</div>
                          <div className="text-muted" style={{fontSize:11}}>{a.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Hours (only for set / add) */}
                  {(bulkForm.action === 'set' || bulkForm.action === 'add') && (
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">
                        {bulkForm.action === 'add' ? 'Hours to Add' : 'Total Hours'} <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input type="number" className="form-control" min="0" max="24" step="0.5"
                          placeholder="e.g. 3"
                          value={bulkForm.hours}
                          onChange={e => { setBulkForm(f=>({...f,hours:e.target.value})); setBulkResult(null); }}/>
                        <span className="input-group-text">hrs</span>
                      </div>
                      {bulkForm.action === 'add' && (
                        <div className="form-text">
                          <i className="bi bi-info-circle me-1"/>
                          This adds on top of each employee's current hours — useful for compensating a mid-day power cut.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Reason */}
                  <div className={bulkForm.action === 'set' || bulkForm.action === 'add' ? 'col-md-8' : 'col-12'}>
                    <label className="form-label fw-semibold">Reason <span className="text-danger">*</span></label>
                    <div className="d-flex flex-wrap gap-1 mb-2">
                      {BULK_REASONS.map(r => (
                        <button key={r} type="button"
                          className={`btn btn-sm ${bulkForm.reason===r?'btn-primary':'btn-outline-secondary'}`}
                          onClick={() => { setBulkForm(f=>({...f,reason:r,customReason:''})); setBulkResult(null); }}>
                          {r}
                        </button>
                      ))}
                    </div>
                    {bulkForm.reason === 'Other' && (
                      <input className="form-control form-control-sm" placeholder="Describe the reason…"
                        value={bulkForm.customReason}
                        onChange={e => setBulkForm(f=>({...f,customReason:e.target.value}))}/>
                    )}
                    {!bulkForm.reason && <div className="text-muted small">Select a quick reason above or choose "Other".</div>}
                  </div>

                  {/* Scope */}
                  <div className="col-12">
                    <label className="form-label fw-semibold">Apply To</label>
                    <div className="d-flex gap-3 align-items-center mb-2">
                      <div className="form-check">
                        <input className="form-check-input" type="radio" id="beAll" checked={bulkSelectAll}
                          onChange={() => { setBulkSelectAll(true); setBulkSelected([]); setBulkResult(null); }}/>
                        <label className="form-check-label" htmlFor="beAll">
                          <strong>All employees</strong> <span className="text-muted small">({staffList.length} staff)</span>
                        </label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="radio" id="beSel" checked={!bulkSelectAll}
                          onChange={() => { setBulkSelectAll(false); setBulkResult(null); }}/>
                        <label className="form-check-label" htmlFor="beSel">Choose specific employees</label>
                      </div>
                    </div>
                    {!bulkSelectAll && (
                      <div className="border rounded p-2" style={{maxHeight:200, overflowY:'auto'}}>
                        {staffList.map(u => (
                          <div key={u._id} className="form-check">
                            <input className="form-check-input" type="checkbox"
                              id={`be-${u._id}`}
                              checked={bulkSelected.includes(u._id)}
                              onChange={e => {
                                setBulkSelected(prev =>
                                  e.target.checked ? [...prev, u._id] : prev.filter(id => id !== u._id)
                                );
                                setBulkResult(null);
                              }}/>
                            <label className="form-check-label small" htmlFor={`be-${u._id}`}>
                              {u.firstName} {u.lastName}
                              <span className="text-muted ms-2" style={{fontSize:11}}>{u.roleKey}</span>
                            </label>
                          </div>
                        ))}
                        {staffList.length === 0 && <div className="text-muted small text-center py-2">No staff loaded.</div>}
                      </div>
                    )}
                  </div>

                  {/* Preview + Submit */}
                  <div className="col-12">
                    <div className="alert alert-warning py-2 small mb-3">
                      <i className="bi bi-exclamation-triangle me-1"/>
                      This will <strong>overwrite</strong> existing attendance records for the selected date and employees.
                      Each employee will receive a notification about the change.
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <button className="btn btn-primary px-4" onClick={submitBulk} disabled={bulkLoading || !bulkForm.date || !bulkForm.reason || (!bulkSelectAll && bulkSelected.length === 0)}>
                        {bulkLoading
                          ? <><span className="spinner-border spinner-border-sm me-2"/>Applying…</>
                          : <><i className="bi bi-check2-all me-2"/>Apply to {bulkSelectAll ? `all ${staffList.length}` : bulkSelected.length} employee{(bulkSelectAll?staffList.length:bulkSelected.length)!==1?'s':''}</>}
                      </button>
                      <button className="btn btn-outline-secondary" onClick={() => { setBulkForm({date:todayISO(),action:'set',status:'present',hours:'',reason:'',customReason:''}); setBulkSelectAll(true); setBulkSelected([]); setBulkResult(null); }}>
                        Reset
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Auto-Fill Missing Days */}
            <div className="card mt-3 border-info">
              <div className="card-header bg-info-subtle fw-semibold">
                <i className="bi bi-calendar-check me-2"/>Auto-Fill Missing Days for a Month
              </div>
              <div className="card-body">
                <p className="small text-muted mb-3">
                  Creates attendance records for all employees for missing days in the selected month:
                  weekend days → <strong>Weekend</strong>, past weekdays with no record → <strong>Leave</strong> or <strong>Absent</strong>.
                </p>
                <div className="row g-3 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Year</label>
                    <select className="form-select" value={autoFillForm.year}
                      onChange={e => setAutoFillForm(p => ({ ...p, year: parseInt(e.target.value) }))}>
                      {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Month</label>
                    <select className="form-select" value={autoFillForm.month}
                      onChange={e => setAutoFillForm(p => ({ ...p, month: parseInt(e.target.value) }))}>
                      {MONTHS.map((mn, i) => <option key={i} value={i+1}>{mn}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Fill absent days as</label>
                    <select className="form-select" value={autoFillForm.fillAbsentAs}
                      onChange={e => setAutoFillForm(p => ({ ...p, fillAbsentAs: e.target.value }))}>
                      <option value="leave">Leave</option>
                      <option value="absent">Absent</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <button className="btn btn-info w-100" onClick={submitAutoFill} disabled={autoFilling}>
                      {autoFilling
                        ? <><span className="spinner-border spinner-border-sm me-1"/>Filling…</>
                        : <><i className="bi bi-calendar-check me-1"/>Auto-Fill Month</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab 4: All Records (admin) ───────────────────────────────────── */}
      {activeTab==='records' && canAdmin && (
        <div>
          <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
            <input className="form-control form-control-sm" style={{width:155}} type="date" value={startDate} onChange={e=>setStartDate(e.target.value)}/>
            <input className="form-control form-control-sm" style={{width:155}} type="date" value={endDate} onChange={e=>setEndDate(e.target.value)}/>
            <select className="form-select form-select-sm" style={{width:200}} value={filterUser} onChange={e=>setFilterUser(e.target.value)}>
              <option value="">All Staff</option>
              {staffList.map(s=><option key={s._id} value={s._id}>{s.firstName} {s.lastName}</option>)}
            </select>
            <button className="btn btn-outline-secondary btn-sm" onClick={fetchRecords}><i className="bi bi-search me-1"/>Filter</button>
            <button className="btn btn-outline-primary btn-sm ms-auto" onClick={()=>setDlgManual(true)}>
              <i className="bi bi-plus me-1"/>Manual Entry
            </button>
          </div>
          <div className="card">
            <div className="card-body p-0">
              {recLoading ? <div className="text-center py-5"><div className="spinner-border text-primary"/></div> : records.length===0 ? (
                <div className="text-center py-5 text-muted"><i className="bi bi-table fs-1 d-block mb-2"/>No records found.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr><th>Date</th><th>Staff</th><th>Status</th><th>First In</th><th>Last Out</th><th>Sessions</th><th>Worked</th><th>Lunch</th><th>Off-Desk</th><th className="text-end">Action</th></tr>
                    </thead>
                    <tbody>
                      {records.map(rec=>(
                        <tr key={rec._id}>
                          <td><div className="fw-semibold small">{fmtDate(rec.date)}</div><small className="text-muted">{new Date(rec.date).toLocaleDateString('en-IN',{weekday:'short'})}</small></td>
                          <td><div className="small">{rec.employee?.firstName} {rec.employee?.lastName}</div><small className="text-muted">{rec.employee?.roleKey}</small></td>
                          <td><span className={`badge bg-${STATUS_BADGE[rec.status]||'secondary'} ${rec.status==='half_day'?'text-dark':''}`} style={rec.status === 'holiday' ? { backgroundColor: STATUS_BG.holiday, color:'#fff' } : undefined}>{rec.leaveLabel || STATUS_LABEL[rec.status] || rec.status?.replace('_',' ')}</span></td>
                          <td className="small">{rec.firstLogin?.time ? fmtTime(rec.firstLogin.time) : '—'}</td>
                          <td className="small">{rec.lastLogout?.time ? fmtTime(rec.lastLogout.time) : '—'}</td>
                          <td>{Array.isArray(rec.sessions) && rec.sessions.length > 0
                            ? rec.sessions.map((s,i)=>(
                                <div key={s._id||i} className="small">
                                  {s.loginTime ? fmtTime(s.loginTime) : '—'}
                                  {s.logoutTime ? ` → ${fmtTime(s.logoutTime)}` : (s.status==='active' ? ' → (active)' : '')}
                                </div>
                              ))
                            : <span className="text-muted small">—</span>
                          }</td>
                          <td className="fw-semibold small">{fmtMins(rec.totalWorkedMinutes||0)}</td>
                          <td className="small text-warning">{rec.lunchMinutes ? fmtMins(rec.lunchMinutes) : '—'}</td>
                          <td className="small text-primary">{rec.officialActivityMinutes ? fmtMins(rec.officialActivityMinutes) : '—'}</td>
                          <td className="text-end"><button className="btn btn-outline-primary btn-sm" onClick={()=>setDlgAdjust(rec)}><i className="bi bi-pencil"/></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          {recTotal>1 && (
            <nav className="mt-3 d-flex justify-content-center">
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${recPage<=1?'disabled':''}`}><button className="page-link" onClick={()=>setRecPage(p=>Math.max(1,p-1))}>‹</button></li>
                {Array.from({length:recTotal},(_,i)=>i+1).map(p=>(
                  <li key={p} className={`page-item ${p===recPage?'active':''}`}><button className="page-link" onClick={()=>setRecPage(p)}>{p}</button></li>
                ))}
                <li className={`page-item ${recPage>=recTotal?'disabled':''}`}><button className="page-link" onClick={()=>setRecPage(p=>Math.min(recTotal,p+1))}>›</button></li>
              </ul>
            </nav>
          )}
        </div>
      )}

      {/* ── Tab 5a: Employee Shifts (admin) ──────────────────────────────── */}
      {activeTab==='employee_shifts' && canAdmin && (
        <div className="row g-3">
          <div className="col-md-7">
            <div className="card">
              <div className="card-header fw-semibold d-flex justify-content-between align-items-center">
                <span><i className="bi bi-clock me-1"/>Per-Employee Shift Overrides</span>
                <span className="text-muted small">{empConfigs.length} configured</span>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Employee</th>
                        <th>Shift</th>
                        <th>Hours/Day</th>
                        <th>Joining</th>
                        <th>Probation</th>
                        <th style={{width:80}}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {empConfigs.length === 0 ? (
                        <tr><td colSpan="6" className="text-center text-muted py-4">
                          No per-user overrides yet. All users follow the tenant policy.
                        </td></tr>
                      ) : empConfigs.map(c => {
                        let probationBadge = <span className="text-muted">—</span>;
                        if (c.joiningDate) {
                          const jd = new Date(c.joiningDate);
                          const months = c.probationMonths ?? 3;
                          const end = new Date(jd); end.setMonth(end.getMonth() + months);
                          const onProb = Date.now() < end.getTime();
                          probationBadge = onProb
                            ? <span className="badge bg-warning text-dark">On probation · until {fmtDate(end)}</span>
                            : <span className="badge bg-success">Confirmed · {fmtDate(end)}</span>;
                        }
                        return (
                          <tr key={c._id}>
                            <td>
                              <div className="fw-semibold">{c.employee?.firstName} {c.employee?.lastName}</div>
                              <div className="small text-muted">{c.employee?.email}</div>
                            </td>
                            <td className="small">
                              {c.shiftStart || <span className="text-muted">—</span>} → {c.shiftEnd || <span className="text-muted">—</span>}
                            </td>
                            <td className="small">{c.requiredHoursPerDay ?? <span className="text-muted">—</span>}</td>
                            <td className="small">{c.joiningDate ? fmtDate(c.joiningDate) : <span className="text-muted">—</span>}</td>
                            <td className="small">{probationBadge}</td>
                            <td>
                              <button className="btn btn-sm btn-outline-primary"
                                      onClick={() => setEmpCfgUser(String(c.employee?._id || c.employee))}>
                                <i className="bi bi-pencil"/>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-5">
            <div className="card">
              <div className="card-header fw-semibold">
                <i className="bi bi-pencil-square me-1"/>{empCfgUser ? 'Edit Shift' : 'Set Shift for Employee'}
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label small">Employee</label>
                  <select className="form-select form-select-sm" value={empCfgUser}
                          onChange={e => setEmpCfgUser(e.target.value)}>
                    <option value="">— Select user —</option>
                    {staffList.filter(u => u.roleKey !== 'student').map(u => (
                      <option key={u._id} value={u._id}>
                        {u.firstName} {u.lastName} ({u.email}) — {u.roleKey}
                      </option>
                    ))}
                  </select>
                </div>

                {empCfgUser && (
                  <>
                    <div className="alert alert-info small py-2 mb-3">
                      Leave a field empty to fall back to the tenant policy default.
                    </div>
                    <div className="row g-2">
                      <div className="col-6">
                        <label className="form-label small">Shift Start</label>
                        <input className="form-control form-control-sm" type="time"
                               value={empCfgForm.shiftStart || ''}
                               onChange={e => setEmpCfgForm(f => ({ ...f, shiftStart: e.target.value }))}/>
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Shift End</label>
                        <input className="form-control form-control-sm" type="time"
                               value={empCfgForm.shiftEnd || ''}
                               onChange={e => setEmpCfgForm(f => ({ ...f, shiftEnd: e.target.value }))}/>
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Required Hours/Day</label>
                        <input className="form-control form-control-sm" type="number" step={0.5}
                               value={empCfgForm.requiredHoursPerDay ?? ''}
                               onChange={e => setEmpCfgForm(f => ({ ...f, requiredHoursPerDay: e.target.value }))}/>
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Grace Period (mins)</label>
                        <input className="form-control form-control-sm" type="number"
                               value={empCfgForm.gracePeriodMinutes ?? ''}
                               onChange={e => setEmpCfgForm(f => ({ ...f, gracePeriodMinutes: e.target.value }))}/>
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Break (mins)</label>
                        <input className="form-control form-control-sm" type="number"
                               value={empCfgForm.breakTimeMinutes ?? ''}
                               onChange={e => setEmpCfgForm(f => ({ ...f, breakTimeMinutes: e.target.value }))}/>
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Monthly Salary</label>
                        <input className="form-control form-control-sm" type="number"
                               value={empCfgForm.monthlySalary ?? ''}
                               onChange={e => setEmpCfgForm(f => ({ ...f, monthlySalary: e.target.value }))}/>
                      </div>
                      <div className="col-6">
                        <label className="form-label small">
                          Joining Date <span className="text-muted">(starts probation clock)</span>
                        </label>
                        <input className="form-control form-control-sm" type="date"
                               value={empCfgForm.joiningDate || ''}
                               onChange={e => setEmpCfgForm(f => ({ ...f, joiningDate: e.target.value }))}/>
                      </div>
                      <div className="col-6">
                        <label className="form-label small">
                          Probation (months) <span className="text-muted">default 3</span>
                        </label>
                        <input className="form-control form-control-sm" type="number" min={0} max={24}
                               value={empCfgForm.probationMonths ?? ''}
                               onChange={e => setEmpCfgForm(f => ({ ...f, probationMonths: e.target.value }))}
                               placeholder="3"/>
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Last Working Date (optional)</label>
                        <input className="form-control form-control-sm" type="date"
                          min={empCfgForm.joiningDate || undefined}
                          value={empCfgForm.lastWorkingDate || ''}
                          onChange={e => setEmpCfgForm(f => ({ ...f, lastWorkingDate: e.target.value }))}/>
                      </div>
                      {empCfgForm.joiningDate && (
                        <div className="col-12">
                          {(() => {
                            const jd = new Date(empCfgForm.joiningDate);
                            const months = parseInt(empCfgForm.probationMonths) || 3;
                            const end = new Date(jd); end.setMonth(end.getMonth() + months);
                            const onProb = Date.now() < end.getTime();
                            return (
                              <div className={`alert ${onProb ? 'alert-warning' : 'alert-success'} small py-2 mb-0`}>
                                {onProb
                                  ? <>On probation until <strong>{fmtDate(end)}</strong>. Paid leave types are blocked until then.</>
                                  : <>Probation completed on <strong>{fmtDate(end)}</strong>. All leave types available.</>}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      <div className="col-12">
                        <label className="form-label small">Notes</label>
                        <textarea className="form-control form-control-sm" rows="2"
                                  value={empCfgForm.notes || ''}
                                  onChange={e => setEmpCfgForm(f => ({ ...f, notes: e.target.value }))}/>
                      </div>
                      <div className="col-12">
                        <div className="form-check form-switch">
                          <input className="form-check-input" type="checkbox" id="deviceLockSwitch"
                                 checked={!!empCfgForm.deviceLock}
                                 onChange={e => setEmpCfgForm(f => ({ ...f, deviceLock: e.target.checked }))}/>
                          <label className="form-check-label small" htmlFor="deviceLockSwitch">
                            <strong>Device Lock</strong> — this user can only clock in from approved devices.
                          </label>
                        </div>
                        {empCfgForm.deviceLock && (
                          <div className="alert alert-info py-2 small mt-2 mb-0">
                            <i className="bi bi-info-circle me-1"/>
                            <strong>After saving:</strong> the employee must attempt a clock-in once (they'll be blocked). A request will then appear in the{' '}
                            <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setActiveTab('devices')}>
                              Authorized Devices tab
                            </button>
                            {' '}for you to approve. The employee can clock in normally once approved.
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="d-flex gap-2 mt-3">
                      <button className="btn btn-primary btn-sm" onClick={saveEmpConfig} disabled={empCfgSaving}>
                        {empCfgSaving ? <span className="spinner-border spinner-border-sm me-1"/> : <i className="bi bi-save me-1"/>}Save
                      </button>
                      <button className="btn btn-outline-danger btn-sm" onClick={removeEmpConfig} disabled={empCfgSaving}>
                        <i className="bi bi-x-circle me-1"/>Remove Override
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab 5b: Authorized Devices (admin) ──────────────────────────── */}
      {activeTab==='devices' && canAdmin && (
        <div>
          <div className="card">
            <div className="card-header d-flex flex-wrap gap-2 align-items-center">
              <span className="fw-semibold"><i className="bi bi-shield-lock me-1"/>Authorized Devices</span>
              <div className="ms-auto d-flex gap-2 align-items-center">
                <select className="form-select form-select-sm" style={{width:140}} value={devicesFilter}
                        onChange={e=>{ setDevicesFilter(e.target.value); }}>
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <button className="btn btn-outline-secondary btn-sm" onClick={fetchDevices}>
                  <i className="bi bi-arrow-clockwise me-1"/>Refresh
                </button>
              </div>
            </div>
            <div className="alert alert-info m-3 mb-0 small py-2">
              Employees whose <strong>Device Lock</strong> is enabled (Employee Shifts) or whose tenant has
              <strong> Require Device Auth</strong> on (Policy Settings) can only clock in from approved devices.
              When an employee tries from a new device, a request appears here as <em>Pending</em>.
            </div>
            <div className="card-body p-0">
              {devicesLoading ? (
                <div className="text-center py-4"><div className="spinner-border text-primary"/></div>
              ) : devices.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-shield-check fs-3 d-block mb-2 text-success"/>
                  No device records found. Enable device locking per-user (Employee Shifts) or globally (Policy Settings) to start tracking.
                </div>
              ) : (() => {
                // Group devices by employee
                const grouped = {};
                devices.forEach(d => {
                  const eid = d.employee?._id || d.employee;
                  if (!grouped[eid]) {
                    grouped[eid] = { employee: d.employee, devices: [] };
                  }
                  grouped[eid].devices.push(d);
                });
                return (
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Employee</th>
                        <th>Devices</th>
                        <th>Statuses</th>
                        <th>Last Seen</th>
                        <th style={{width:130}}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(grouped).map(({ employee, devices: empDevices }) => {
                        const approved = empDevices.filter(d => d.status === 'approved').length;
                        const pending = empDevices.filter(d => d.status === 'pending').length;
                        const rejected = empDevices.filter(d => d.status === 'rejected').length;
                        const latest = empDevices.find(d => d.lastSeenAt);
                        return (
                        <tr key={employee?._id || 'unknown'}>
                          <td>
                            <div className="fw-semibold small">{employee?.firstName} {employee?.lastName}</div>
                            <div className="text-muted" style={{fontSize:11}}>{employee?.email}</div>
                            <div className="text-muted" style={{fontSize:11}}>{employee?.roleKey}</div>
                          </td>
                          <td>
                            <span className="badge bg-primary me-1">{empDevices.length} device{empDevices.length !== 1 ? 's' : ''}</span>
                            {empDevices.map(d => (
                              <div key={d._id} className="small text-muted" style={{fontSize:11}}>
                                {d.deviceInfo?.browser || d.deviceName || 'Unknown'} · {d.deviceInfo?.os || '—'}
                              </div>
                            ))}
                          </td>
                          <td>
                            {approved > 0 && <span className="badge bg-success me-1">{approved} approved</span>}
                            {pending > 0 && <span className="badge bg-warning text-dark me-1">{pending} pending</span>}
                            {rejected > 0 && <span className="badge bg-danger me-1">{rejected} rejected</span>}
                          </td>
                          <td className="small">{latest?.lastSeenAt ? fmtDate(latest.lastSeenAt) : '—'}</td>
                          <td>
                            <div className="d-flex gap-1">
                              {pending > 0 && (
                                <button className="btn btn-sm btn-success" title="Approve all pending" onClick={() => empDevices.filter(d => d.status === 'pending').forEach(d => approveDevice(d._id))}>
                                  <i className="bi bi-check-all me-1"/>Approve All
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab 5b: Clock-Out Correction Requests (admin) ────────────────── */}
      {activeTab==='corrections' && canAdmin && (
        <div className="row g-3">
          <div className="col-12">
            <div className="card">
              <div className="card-header fw-semibold d-flex justify-content-between align-items-center">
                <span><i className="bi bi-pencil-square me-1"/>Clock-Out Correction Requests</span>
                <span className="badge bg-warning text-dark">{correctionRequests.length} pending</span>
              </div>
              <div className="card-body p-0">
                {correctionRequests.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-check-circle fs-1 d-block mb-2"/>
                    No pending correction requests.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Employee</th>
                          <th>Last Interaction</th>
                          <th>Original Time</th>
                          <th>Requested Time</th>
                          <th>Reason</th>
                          <th>Submitted</th>
                          <th className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {correctionRequests.map(req => (
                          <tr key={req._id}>
                            <td>
                              <div className="fw-semibold small">{req.employee?.firstName} {req.employee?.lastName}</div>
                              <div className="text-muted small">{req.employee?.email}</div>
                            </td>
                            <td className="small">{req.lastActivityTime ? fmtTime(req.lastActivityTime) : '—'} <span className="text-muted">{fmtDate(req.lastActivityTime)}</span></td>
                            <td className="small">{req.originalLogoutTime ? fmtTime(req.originalLogoutTime) : '—'} <span className="text-muted">{fmtDate(req.originalLogoutTime)}</span></td>
                            <td className="small text-success fw-semibold">{req.requestedLogoutTime ? fmtTime(req.requestedLogoutTime) : '—'} <span className="text-muted">{fmtDate(req.requestedLogoutTime)}</span></td>
                            <td className="small" style={{maxWidth:200}}>{req.reason}</td>
                            <td className="small text-muted">{fmtDate(req.createdAt)} {fmtTime(req.createdAt)}</td>
                            <td className="text-end">
                              <div className="d-flex gap-1 justify-content-end">
                                <button className="btn btn-sm btn-success" title="Approve"
                                  onClick={() => reviewCorrection(req._id, 'approved')}>
                                  <i className="bi bi-check-lg"/>Approve
                                </button>
                                <button className="btn btn-sm btn-danger" title="Reject"
                                  onClick={() => reviewCorrection(req._id, 'rejected')}>
                                  <i className="bi bi-x-lg"/>Reject
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              {correctionTotal > 1 && (
                <div className="card-footer d-flex justify-content-center">
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${correctionPage <= 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCorrectionPage(p => Math.max(1, p - 1))}>‹</button>
                    </li>
                    {Array.from({ length: correctionTotal }, (_, i) => i + 1).map(p => (
                      <li key={p} className={`page-item ${p === correctionPage ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => setCorrectionPage(p)}>{p}</button>
                      </li>
                    ))}
                    <li className={`page-item ${correctionPage >= correctionTotal ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCorrectionPage(p => Math.min(correctionTotal, p + 1))}>›</button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab 5c: Monthly Reports (admin) ──────────────────────────────── */}
      {activeTab==='monthly_reports' && canAdmin && (
        <div className="row g-3">
          {!mrOpen && (
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex flex-wrap gap-2 align-items-center">
                  <span className="fw-semibold"><i className="bi bi-file-earmark-spreadsheet me-1"/>Monthly Attendance Reports</span>
                  <div className="ms-auto d-flex gap-2 align-items-center">
                    <select className="form-select form-select-sm" style={{width:200}}
                            value={mrFilterEmployee}
                            onChange={e => setMrFilterEmployee(e.target.value)}>
                      <option value="">All Employees</option>
                      {mrReports.map(r => (
                        <option key={r.employee?._id || r.employee} value={r.employee?._id || r.employee}>
                          {r.employee?.firstName} {r.employee?.lastName}
                        </option>
                      ))}
                    </select>
                    <select className="form-select form-select-sm" style={{width:130}}
                            value={mrMonth} onChange={e => setMrMonth(parseInt(e.target.value))}>
                      {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
                    </select>
                    <select className="form-select form-select-sm" style={{width:100}}
                            value={mrYear} onChange={e => setMrYear(parseInt(e.target.value))}>
                      {Array.from({length:5},(_,i)=>new Date().getFullYear()-i).map(y =>
                        <option key={y} value={y}>{y}</option>)}
                    </select>
                    <button className="btn btn-primary btn-sm" disabled={mrGenerating}
                            onClick={() => generateMonthlyReports()}>
                      {mrGenerating
                        ? <span className="spinner-border spinner-border-sm me-1"/>
                        : <i className="bi bi-arrow-clockwise me-1"/>}
                      Generate / Refresh All
                    </button>
                    <button className="btn btn-success btn-sm" disabled={mrReports.length === 0}
                            onClick={downloadMonthlyReportPDF}>
                      <i className="bi bi-file-earmark-pdf me-1"/>Download PDF
                    </button>
                    <button className="btn btn-outline-primary btn-sm"
                            disabled={mrReports.length === 0 || mrSendingAdmin}
                            onClick={sendMonthlyReportToAdmins}>
                      {mrSendingAdmin
                        ? <span className="spinner-border spinner-border-sm me-1"/>
                        : <i className="bi bi-send me-1"/>}
                      Send to Admin
                    </button>
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="alert alert-info m-3 mb-0 small py-2">
                    Reports auto-generate at the start of each new month. You can also manually generate or refresh
                    any month above. Use the clock action to review a report and correct clock-out time when required.
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Employee</th>
                          <th className="text-end">Days in Month</th>
                          <th className="text-end">Present Days</th>
                          <th className="text-end">Leave / Absent</th>
                          <th className="text-end">Late Login</th>
                          <th className="text-end">Permission</th>
                          <th className="text-end">Excess Permission</th>
                          <th style={{width:140}}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mrLoading ? (
                          <tr><td colSpan="8" className="text-center py-4">
                            <div className="spinner-border spinner-border-sm"/>
                          </td></tr>
                        ) : mrReports.length === 0 ? (
                          <tr><td colSpan="8" className="text-center text-muted py-4">
                            No reports for {MONTHS[mrMonth-1]} {mrYear}. Click "Generate / Refresh All" to build them.
                          </td></tr>
                        ) : (() => {
                          const filtered = mrFilterEmployee ? mrReports.filter(r => String(r.employee?._id || r.employee) === mrFilterEmployee) : mrReports;
                          return filtered.length === 0 ? (
                            <tr><td colSpan="8" className="text-center text-muted py-4">
                              No reports found for the selected employee.
                            </td></tr>
                          ) : filtered.map(r => (
                          <tr key={r._id}>
                            <td>
                              <div className="fw-semibold">{r.employee?.firstName} {r.employee?.lastName}</div>
                              <div className="small text-muted">{r.employee?.email}</div>
                            </td>
                            <td className="text-end">{r.workingDays ?? new Date(r.year, r.month, 0).getDate()}</td>
                            <td className="text-end">{Number(r.presentDays || 0)}</td>
                            <td className="text-end">{Number(r.absentDays ?? r.totalLeaveDays ?? 0)}</td>
                            <td className="text-end">{(Number(r.lateLoginMinutes || 0) / 60).toFixed(1)}h</td>
                            <td className="text-end">{Number(r.permissionHoursUsed || 0).toFixed(1)}h</td>
                            <td className="text-end">{Number(r.excessPermissionHours || 0).toFixed(1)}h</td>
                            <td>
                              <button className="btn btn-sm btn-outline-primary me-1"
                                      title="Review report and correct clock-out"
                                      onClick={() => openReport(r._id)}>
                                <i className="bi bi-clock-history"/>
                              </button>
                              <button className="btn btn-sm btn-outline-secondary me-1"
                                      title="Refresh this report"
                                      disabled={mrGenerating}
                                      onClick={() => generateMonthlyReports(r.employee?._id || r.employee)}>
                                <i className="bi bi-arrow-clockwise"/>
                              </button>
                              <button className="btn btn-sm btn-outline-info"
                                      title="Send report to employee via email"
                                      onClick={() => setDlgSendEmail({ reportId: r._id, employeeName: `${r.employee?.firstName} ${r.employee?.lastName || ''}`.trim() })}>
                                <i className="bi bi-envelope"/>
                              </button>
                              <button className="btn btn-sm btn-outline-danger ms-1"
                                      title="Remove this monthly report"
                                      disabled={mrDeletingId === r._id}
                                      onClick={() => deleteMonthlyReport(r)}>
                                {mrDeletingId === r._id
                                  ? <span className="spinner-border spinner-border-sm"/>
                                  : <i className="bi bi-trash"/>}
                              </button>
                            </td>
                          </tr>
                        ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Drill-in detail */}
          {mrOpen && (
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex flex-wrap gap-2 align-items-center">
                  <button className="btn btn-sm btn-outline-secondary me-2" disabled={mrSaving || mrFinalizing} onClick={() => setMrOpen(null)}>
                    <i className="bi bi-arrow-left me-1"/>Back
                  </button>
                  <span className="fw-semibold">
                    {mrOpen.employee?.firstName} {mrOpen.employee?.lastName}
                    <span className="text-muted ms-2">— {MONTHS[mrOpen.month-1]} {mrOpen.year}</span>
                  </span>
                  {mrOpen.isOnProbation && (
                    <span className="badge bg-warning text-dark ms-2">On probation</span>
                  )}
                  <div className="ms-auto d-flex gap-2">
                    {mrOpen.status === 'draft'
                      ? <button className="btn btn-sm btn-success" onClick={finalizeReport} disabled={!!mrClockoutEntry || mrSaving || mrFinalizing}
                          title={mrClockoutEntry ? 'Save or cancel the open correction first' : 'Finalize saved attendance'}>
                          <i className="bi bi-lock me-1"/>Finalize
                        </button>
                      : <button className="btn btn-sm btn-outline-warning" onClick={reopenReport} disabled={mrSaving || mrFinalizing}>
                          <i className="bi bi-unlock me-1"/>Reopen
                        </button>}
                  </div>
                </div>
                <div className="card-body">
                  <div className="row g-2 mb-3">
                    {[
                      ['Total Days',   new Date(mrOpen.year, mrOpen.month, 0).getDate()],
                      ['Present',      Number(mrOpen.presentDays || 0)],
                      ['Leave / Absent', Number(mrOpen.absentDays ?? mrOpen.totalLeaveDays ?? 0)],
                      ['Half Days',    mrOpen.halfDays],
                      ['Holidays',     mrOpen.holidayDays || 0],
                      ['Hours',        fmtMins(mrOpen.totalWorkedMinutes)],
                      ['Permission',   `${mrOpen.permissionHoursUsed || 0}h`],
                      ['Excess',       `${mrOpen.excessPermissionHours || 0}h`],
                    ].map(([l, v]) => (
                      <div key={l} className="col"><div className="border rounded p-2 text-center">
                        <div className="small text-muted">{l}</div>
                        <div className="fw-semibold">{v}</div>
                      </div></div>
                    ))}
                  </div>

                  {mrOpen.leaveSummary && Object.keys(mrOpen.leaveSummary).length > 0 && (
                    <div className="mb-3">
                      <div className="small text-muted mb-1">Leave Summary</div>
                      <div className="d-flex flex-wrap gap-2">
                        {Object.entries(mrOpen.leaveSummary).map(([k, v]) => {
                          const def = mrLeaveTypes.find(x => x.key === k);
                          return (
                            <span key={k} className="badge"
                                  style={{ background: def?.color || '#6c757d', color: '#fff' }}>
                              {def?.label || k}: {v}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <h6 className="mt-3 mb-2">Day-by-Day Attendance</h6>
                  <div className="d-flex gap-3 mb-2 small flex-wrap">
                    <span><i className="bi bi-circle-fill text-success me-1"></i>Present</span>
                    <span><i className="bi bi-circle-fill text-danger me-1"></i>Absent</span>
                    <span><i className="bi bi-circle-fill text-info me-1"></i>Full Day Leave</span>
                    <span><i className="bi bi-circle-fill text-warning me-1"></i>Half Day</span>
                    <span><i className="bi bi-circle-fill text-secondary me-1"></i>Weekend</span>
                  </div>
                    <div className="table-responsive">
                    <table className="table table-sm table-bordered align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{width:100}}>Date</th>
                          <th style={{width:50}}>Day</th>
                          <th style={{width:80}}>Status</th>
                          <th style={{width:70}}>In</th>
                          <th style={{width:70}}>Out</th>
                          <th className="text-end" style={{width:70}}>Worked</th>
                          <th style={{width:120}}>Clock-Out Correction</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mrCalendarDays.length === 0 ? (
                          <tr><td colSpan="7" className="text-center py-4"><div className="spinner-border spinner-border-sm me-2"/>Loading days...</td></tr>
                        ) : mrCalendarDays.map(day => {
                          const rec = mrDayRecords[day.date];
                          const leaveEntry = (mrOpen.leaveEntries || []).find(e => {
                            const eDate = e.date instanceof Date ? e.date.toISOString().slice(0, 10) : String(e.date).split('T')[0];
                            return eDate === day.date;
                          });
                          const isHolidayDay = day.status === 'holiday';
                          const badgeStatus = day.status;
                          const badgeLabel = isHolidayDay ? 'Holiday' : day.leaveLabel || (day.status === 'future' ? '' : STATUS_LABEL[day.status] || day.status?.replace('_', ' '));
                          const statusBadgeClass = badgeStatus === 'holiday' ? '' : ({ present:'success', absent:'danger', half_day:'warning', leave:'info', weekend:'secondary', future:'light', permission:'info' }[badgeStatus] || 'secondary');
                          const canCorrectAttendance = mrOpen.status !== 'finalized'
                            && day.status !== 'future'
                            && !['holiday', 'weekend'].includes(badgeStatus);

                          return (
                            <tr key={day.date} className={day.isToday ? 'table-active' : ''}>
                              <td className="small">{new Date(day.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                              <td className="small text-muted">{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][day.dayOfWeek]}</td>
                              <td>
                                {day.status === 'future' && !leaveEntry ? (
                                  <span className="text-muted small">—</span>
                                ) : badgeStatus === 'holiday' ? (
                                  <span className="badge" style={{ background: '#6f42c1', color: '#fff' }}>
                                    {badgeLabel}
                                  </span>
                                ) : (
                                  <span className={`badge bg-${statusBadgeClass} ${badgeStatus === 'half_day' ? 'text-dark' : ''}`}>
                                    {badgeLabel}
                                  </span>
                                )}
                              </td>
                              {/* In */}
                              <td className="small">
                                {rec?.firstLogin?.time
                                  ? fmtTime(rec.firstLogin.time)
                                  : <span className="text-muted">—</span>}
                              </td>
                              {/* Out */}
                              <td className="small">
                                {mrClockoutEntry?.date === day.date ? (
                                  <div className="d-flex flex-column gap-1">
                                    <select className="form-select form-select-sm" style={{width:150}}
                                      aria-label="Attendance status" disabled={mrSaving || mrFinalizing} value={mrClockoutEntry.attendanceStatus || 'auto'}
                                      onChange={e => setMrClockoutEntry({ ...mrClockoutEntry, attendanceStatus: e.target.value })}>
                                      <option value="auto">From hours / leave</option>
                                      <option value="present">Present</option>
                                      <option value="half_day">Half Day</option>
                                      <option value="absent">Absent</option>
                                    </select>
                                    <input type="time" className="form-control form-control-sm" style={{width:110}}
                                      disabled={mrSaving || mrFinalizing} value={mrClockoutEntry.clockInTime || ''}
                                      onChange={e => setMrClockoutEntry({ ...mrClockoutEntry, clockInTime: e.target.value })}
                                      title="Clock-in time (optional)"/>
                                    <input type="time" className="form-control form-control-sm" style={{width:110}}
                                      disabled={mrSaving || mrFinalizing} value={mrClockoutEntry.clockOutTime || ''}
                                      onChange={e => setMrClockoutEntry({ ...mrClockoutEntry, clockOutTime: e.target.value })}
                                      title="Clock-out time (required)"/>
                                    <div className="d-flex gap-1">
                                      <button className="btn btn-sm btn-success" disabled={mrSaving || mrFinalizing} onClick={() => handleSetClockout(mrClockoutEntry)}>
                                        <i className="bi bi-check"/></button>
                                      <button className="btn btn-sm btn-outline-secondary" disabled={mrSaving || mrFinalizing} onClick={() => setMrClockoutEntry(null)}>
                                        <i className="bi bi-x"/></button>
                                    </div>
                                  </div>
                                ) : rec?.lastLogout?.time ? (
                                  fmtTime(rec.lastLogout.time)
                                ) : mrOpen.status === 'finalized' ? (
                                  <span className="text-muted">-</span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              {/* Worked */}
                              <td className="text-end small">{fmtMins(rec?.totalWorkedMinutes || 0)}</td>
                              {/* Action */}
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  disabled={!canCorrectAttendance || mrSaving || mrFinalizing || mrClockoutEntry?.date === day.date}
                                  title={rec ? 'Correct attendance time' : 'Add missing attendance'}
                                  onClick={() => setMrClockoutEntry({
                                    date: day.date,
                                    attendanceStatus: rec?.manualAttendanceStatus || 'auto',
                                    originalClockInTime: rec?.firstLogin?.time ? inputTime(rec.firstLogin.time) : '',
                                    originalClockOutTime: rec?.lastLogout?.time ? inputTime(rec.lastLogout.time) : '',
                                    clockInTime: rec?.firstLogin?.time ? inputTime(rec.firstLogin.time) : '',
                                    clockOutTime: rec?.lastLogout?.time ? inputTime(rec.lastLogout.time) : '',
                                    notes: rec ? 'Admin corrected attendance time' : 'Admin added missing attendance',
                                  })}>
                                  <i className={`bi ${rec ? 'bi-clock-history' : 'bi-plus-circle'} me-1`}/>{rec ? 'Correct' : 'Add'}
                                </button>
                              </td>
                            </tr>
                          );
                        })});
                      </tbody>
                    </table>
                  </div>

                  {mrOpen.auditLog && mrOpen.auditLog.length > 0 && (
                    <details className="mt-3">
                      <summary className="text-muted small">Audit log ({mrOpen.auditLog.length})</summary>
                      <div className="small mt-2">
                        {mrOpen.auditLog.slice().reverse().map((a, i) => (
                          <div key={i} className="border-bottom py-1">
                            <span className="badge bg-light text-dark me-2">{a.action}</span>
                            <span className="text-muted">{fmtDate(a.performedAt)}</span>
                            {a.note && <span className="ms-2">— {a.note}</span>}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab 5: Policy (admin) ────────────────────────────────────────── */}
      {activeTab==='policy' && canAdmin && (
        <div className="card" style={{maxWidth:600}}>
          <div className="card-header fw-semibold"><i className="bi bi-sliders me-1"/>Work Policy Settings</div>
          <div className="card-body">
            {!policy ? <div className="text-center py-3"><div className="spinner-border text-primary"/></div> : (
              <>
                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <label className="form-label">Shift Start (Default)</label>
                    <input className="form-control" type="time" value={polForm.shiftStart||''} onChange={e=>setPolForm(p=>({...p,shiftStart:e.target.value}))}/>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Shift End (Default)</label>
                    <input className="form-control" type="time" value={polForm.shiftEnd||''} onChange={e=>setPolForm(p=>({...p,shiftEnd:e.target.value}))}/>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Grace Period (mins)</label>
                    <input className="form-control" type="number" value={polForm.gracePeriodMins||''} onChange={e=>setPolForm(p=>({...p,gracePeriodMins:e.target.value}))}/>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Full Day Hours</label>
                    <input className="form-control" type="number" step={0.5} value={polForm.requiredHoursPerDay??8} onChange={e=>setPolForm(p=>({...p,requiredHoursPerDay:e.target.value}))}/>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Half Day Hours</label>
                    <input className="form-control" type="number" step={0.5} value={polForm.halfDayThresholdHours??4} onChange={e=>setPolForm(p=>({...p,halfDayThresholdHours:e.target.value}))}/>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Lunch Break (mins)</label>
                    <input className="form-control" type="number" value={polForm.lunchBreakMins??60} onChange={e=>setPolForm(p=>({...p,lunchBreakMins:e.target.value}))}/>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Permission Hours/Month</label>
                    <input className="form-control" type="number" step={0.5} value={polForm.permissionHoursPerMonth??4} onChange={e=>setPolForm(p=>({...p,permissionHoursPerMonth:e.target.value}))}/>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Weekend Days</label>
                  <div className="d-flex flex-wrap gap-3">
                    {DAYS.map((d,i)=>(
                      <div key={i} className="form-check">
                        <input className="form-check-input" type="checkbox" id={`wd${i}`}
                          checked={(polForm.weekendDays||[]).includes(i)}
                          onChange={e=>setPolForm(p=>({...p,weekendDays:e.target.checked?[...(p.weekendDays||[]),i]:(p.weekendDays||[]).filter(x=>x!==i)}))}/>
                        <label className="form-check-label" htmlFor={`wd${i}`}>{d}</label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mb-3">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="requireDeviceAuth"
                           checked={!!polForm.requireDeviceAuth}
                           onChange={e=>setPolForm(p=>({...p,requireDeviceAuth:e.target.checked}))}/>
                    <label className="form-check-label" htmlFor="requireDeviceAuth">
                      <strong>Require Device Authentication</strong> — all employees must clock in from an admin-approved device.
                      <div className="small text-muted">Individual override available in Employee Shifts → Device Lock.</div>
                    </label>
                  </div>
                </div>
                <button className="btn btn-primary" onClick={savePolicy} disabled={polSaving}>
                  {polSaving?<span className="spinner-border spinner-border-sm me-1"/>:<i className="bi bi-save me-1"/>}Save Policy
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Tab 6: Manual / Instructions (all users) ─────────────────────── */}
      {activeTab==='manual' && (
        <PolicyTab moduleKey="work-attendance" label="Work Attendance Manual / Instructions" />
      )}

      {/* ── Dialogs ──────────────────────────────────────────────────────── */}
      <AdjustDialog
        show={!!dlgAdjust} record={dlgAdjust} onClose={()=>setDlgAdjust(null)}
        onSuccess={()=>{ fetchRecords(); showAlert('Record adjusted.','success'); }}
        onAlert={showAlert}
      />
      <ActivityApproveDialog
        show={!!dlgApprove} activity={dlgApprove} onClose={()=>setDlgApprove(null)}
        onSuccess={()=>{ fetchActivities(); showAlert('Activity reviewed.','success'); }}
        onAlert={showAlert}
      />
      <ManualEntryDialog
        show={dlgManual} onClose={()=>setDlgManual(false)} staffList={staffList}
        onSuccess={()=>{ fetchRecords(); showAlert('Manual entry saved.','success'); }}
        onAlert={showAlert}
      />

      {/* Fix Logout Dialog */}
      <BSModal show={!!dlgFixLogout} onClose={() => setDlgFixLogout(null)} title="Fix Missing Clock-Out"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setDlgFixLogout(null)}>Cancel</button>
          <button className="btn btn-warning" onClick={() => {
            if (!dlgFixLogout?.logoutTime) return showAlert('Enter the logout time.', 'warning');
            fixLogout({ sessionId: dlgFixLogout.sessionId, logoutTime: dlgFixLogout.logoutTime, warningNote: dlgFixLogout.warningNote });
            setDlgFixLogout(null);
          }}>Submit Correction Request</button>
        </>}>
        {dlgFixLogout && (
          <div>
            <div className="alert alert-warning py-2 small mb-3">
              <i className="bi bi-exclamation-triangle me-1"/>
              <strong>{dlgFixLogout.employeeName}</strong> clocked in at <strong>{fmtTime(dlgFixLogout.loginTime)}</strong> and never clocked out.
              This will create a correction request that the employee must approve.
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Suggested Logout Time <span className="text-danger">*</span></label>
              <input type="datetime-local" className="form-control"
                value={dlgFixLogout.logoutTime || ''}
                onChange={e => setDlgFixLogout(p => ({ ...p, logoutTime: e.target.value }))}/>
              <div className="form-text">The employee will see this suggested time and can approve or reject it.</div>
            </div>
            <div>
              <label className="form-label fw-semibold">Reason for Correction</label>
              <textarea className="form-control" rows={2}
                value={dlgFixLogout.warningNote || ''}
                onChange={e => setDlgFixLogout(p => ({ ...p, warningNote: e.target.value }))}
                placeholder="e.g. Employee forgot to clock out. Please review and approve."/>
            </div>
          </div>
        )}
      </BSModal>

      {/* Self-Fix Logout Dialog — employee closes their own stale session */}
      <BSModal show={!!dlgSelfFix} onClose={() => setDlgSelfFix(null)} title="Fix My Missed Clock-Out"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setDlgSelfFix(null)} disabled={selfFixing}>Cancel</button>
          <button className="btn btn-danger" onClick={submitSelfFix} disabled={selfFixing}>
            {selfFixing ? <><span className="spinner-border spinner-border-sm me-1"/>Saving…</> : <><i className="bi bi-check-circle me-1"/>Submit</>}
          </button>
        </>}>
        {dlgSelfFix && (
          <div>
            <div className="alert alert-warning py-2 small mb-3">
              <i className="bi bi-info-circle me-1"/>
              Enter your actual logout time and reason below. Your attendance will be recalculated and your admin will be notified.
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">My Actual Logout Time <span className="text-danger">*</span></label>
              <input type="datetime-local" className="form-control"
                value={dlgSelfFix.logoutTime || ''}
                onChange={e => setDlgSelfFix(p => ({ ...p, logoutTime: e.target.value }))}/>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Reason for Missed Clock-Out <span className="text-danger">*</span></label>
              <textarea className="form-control" rows={2}
                value={dlgSelfFix.reason || ''}
                onChange={e => setDlgSelfFix(p => ({ ...p, reason: e.target.value }))}
                placeholder="e.g. Forgot to clock out, was in a hurry, emergency situation..."
                required/>
              <div className="small text-muted mt-1">This reason will be visible to your admin and will be recorded for attendance compliance.</div>
            </div>
          </div>
        )}
      </BSModal>

      {/* Add Session Dialog — admin creates a manual work session for an employee */}
      <BSModal show={!!dlgAddSession} onClose={() => setDlgAddSession(null)} title="Add Work Session"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setDlgAddSession(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={submitAddSession}>
            <i className="bi bi-check-circle me-1"/>Create Session
          </button>
        </>}>
        {dlgAddSession && (
          <div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Employee</label>
              <select className="form-select"
                value={dlgAddSession.employeeId || ''}
                onChange={e => setDlgAddSession(p => ({ ...p, employeeId: e.target.value }))}>
                <option value="">Select employee</option>
                {staffList.map(s => <option key={s._id} value={s._id}>{s.firstName} {s.lastName}</option>)}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Login Time <span className="text-danger">*</span></label>
              <input type="datetime-local" className="form-control"
                value={dlgAddSession.loginTime || ''}
                onChange={e => setDlgAddSession(p => ({ ...p, loginTime: e.target.value }))}/>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Logout Time <span className="text-muted">(optional)</span></label>
              <input type="datetime-local" className="form-control"
                value={dlgAddSession.logoutTime || ''}
                onChange={e => setDlgAddSession(p => ({ ...p, logoutTime: e.target.value }))}/>
              <div className="form-text">Leave empty if employee is still clocked in.</div>
            </div>
            <div>
              <label className="form-label fw-semibold">Notes</label>
              <input type="text" className="form-control"
                value={dlgAddSession.notes || ''}
                onChange={e => setDlgAddSession(p => ({ ...p, notes: e.target.value }))}
                placeholder="e.g. Manually added by admin"/>
            </div>
          </div>
        )}
      </BSModal>

      {/* Clock-Out Correction Request Dialog */}
      <BSModal show={!!dlgCorrection} onClose={() => setDlgCorrection(null)} title="Request Clock-Out Correction"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setDlgCorrection(null)} disabled={correctionSubmitting}>Cancel</button>
          <button className="btn btn-primary" onClick={submitCorrection} disabled={correctionSubmitting}>
            {correctionSubmitting ? <><span className="spinner-border spinner-border-sm me-1"/>Submitting…</> : <><i className="bi bi-send me-1"/>Submit Request</>}
          </button>
        </>}>
        {dlgCorrection && (
          <div>
            <div className="alert alert-info py-2 small mb-3">
              <i className="bi bi-info-circle me-1"/>
              Your auto clock-out time was: <strong>{dlgCorrection.logoutTime ? fmtTime(dlgCorrection.logoutTime) : '—'}</strong> on {dlgCorrection.logoutTime ? fmtDate(dlgCorrection.logoutTime) : '—'}.
              Please provide the correct time you actually stopped working and a reason for the correction.
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Requested Clock-Out Time <span className="text-danger">*</span></label>
              <input type="datetime-local" className="form-control"
                defaultValue={dlgCorrection.logoutTime ? new Date(dlgCorrection.logoutTime).toISOString().slice(0, 16) : ''}
                onChange={e => setDlgCorrection(p => ({ ...p, requestedTime: e.target.value }))}/>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Reason <span className="text-danger">*</span></label>
              <textarea className="form-control" rows={3}
                value={dlgCorrection.reason || ''}
                onChange={e => setDlgCorrection(p => ({ ...p, reason: e.target.value }))}
                placeholder="Please explain why you need this correction (e.g., was in a meeting, system issue, etc.)"/>
            </div>
          </div>
        )}
      </BSModal>

      {/* Idle Warning Dialog */}
      <IdleWarningDialog
        show={idleWarningShown}
        lastActivity={lastActivityTime}
        onDismiss={handleIdleDismiss}
        onClockOut={handleIdleClockOut}
      />

      {/* Send Report Email Dialog */}
      <SendReportEmailDialog
        show={!!dlgSendEmail}
        data={dlgSendEmail}
        onClose={() => setDlgSendEmail(null)}
        onSuccess={() => showAlert('Email sent successfully.', 'success')}
        onAlert={(msg, type) => showAlert(msg, type)}
      />
    </div>
  );
}
